import * as i3 from '@polaris-customerweb/sharedcomponents.core';
import { UserContentColorHex, CacheCollectionObservable, defaultCulture } from '@polaris-customerweb/sharedcomponents.core';
import * as i2 from '@polaris-customerweb/sharedcomponents.shared';
import { GeoBounds, GeoCoordinate, GeoJsonPolygon, GeoJsonPoint, GeoJsonLineString, GeoJsonFeatureCollection, ButtonComponent, UuidService, Image } from '@polaris-customerweb/sharedcomponents.shared';
import { kinks } from '@turf/kinks';
import { cloneDeep, isEqual, defaultsDeep, flatten, includes, findIndex, get, sortBy, intersection, first } from 'lodash-es';
import { of, map } from 'rxjs';
import { NgClass, formatNumber } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, HostListener, ViewChild, Output, Input, Component, Injectable, signal, input, output, computed } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import * as i1 from '@polaris-customerweb/sharedcomponents.config';
import { toSignal } from '@angular/core/rxjs-interop';
import * as i2$1 from '@polaris-customerweb/sharedcomponents.user-profile';
import { HttpParams } from '@angular/common/http';
import * as i1$1 from '@polaris-customerweb/sharedcomponents.auth';
import { switchMap, catchError } from 'rxjs/operators';

class Geofence {
    static { this.defaultMaxPoints = 25; }
    constructor(contract) {
        this.type = 'Geofence';
        this._maxPoints = Geofence.defaultMaxPoints;
        if (contract) {
            this.contract = cloneDeep(contract);
        }
        else {
            this.contract = {
                snapshots: [],
                type: 'Geofence',
            };
        }
    }
    get area() {
        return this.properties.area || 0;
    }
    get bounds() {
        if (!this._bounds) {
            this._bounds = GeoBounds.fromCoordinateArrayPath(this.coordinates);
        }
        return this._bounds;
    }
    get color() {
        return this.properties.color;
    }
    set color(color) {
        this.properties.color = color;
    }
    get coordinates() {
        return this.feature.geometry.coordinates[0] || [];
    }
    set coordinates(coordinates) {
        this.feature.geometry.coordinates[0] = [...coordinates];
        this._polyLine = undefined;
        this._bounds = undefined;
    }
    get creationDate() {
        if (!this._creationDate && this.contract.creationDate) {
            this._creationDate = new Date(this.contract.creationDate);
        }
        return this._creationDate ?? new Date();
    }
    get description() {
        return this.properties.description || '';
    }
    set description(description) {
        this.properties.description = description;
    }
    get geoCoordinate() {
        return this.bounds.centerGeoCoordinate;
    }
    get id() {
        return this.contract.id ?? '';
    }
    set id(id) {
        this.contract.id = id;
    }
    get maxPoints() {
        return this._maxPoints;
    }
    set maxPoints(maxPoints) {
        this._maxPoints = maxPoints;
    }
    get modificationDate() {
        if (!this._modificationDate && this.contract.modificationDate) {
            this._modificationDate = new Date(this.contract.modificationDate);
        }
        return this._modificationDate;
    }
    set modificationDate(date) {
        this._modificationDate = date;
        this.contract.modificationDate = date?.toISOString();
    }
    get name() {
        return this.properties.name || '';
    }
    set name(name) {
        this.properties.name = name;
    }
    get ownerEmail() {
        return this.contract.ownerEmail || '';
    }
    get polygon() {
        if (!this._polyLine) {
            this._polyLine = this.coordinates.map((coord) => GeoCoordinate.fromCoordinateArray(coord));
        }
        return this._polyLine;
    }
    get riderId() {
        return this.contract.riderId || '';
    }
    /** @deprecated - Use shown on map service */
    get shownOnMap() {
        return true;
    }
    /** @deprecated */
    set shownOnMap(_shownOnMap) {
        // do nothing
    }
    get snapshots() {
        return [...(this.contract.snapshots || [])];
    }
    get feature() {
        return this.featureCollection.features[0];
    }
    get featureCollection() {
        if ((this.contract.featureCollection?.features ?? []).length < 1) {
            this.contract.featureCollection = {
                features: [
                    new GeoJsonPolygon([], {
                        featureType: 'Geofence',
                    }),
                ],
                type: 'FeatureCollection',
            };
        }
        return this.contract.featureCollection;
    }
    get properties() {
        return this.feature.properties;
    }
    clearArea() {
        this.coordinates = [];
    }
    addPoint(geoCoordinate) {
        this.coordinates = [...this.coordinates, geoCoordinate.toCoordinateArray()];
    }
    clone() {
        return new Geofence(this.contract);
    }
    toContract() {
        return cloneDeep(this.contract);
    }
    toEditableGeoJson() {
        const features = [];
        if (this.coordinates.length === 1) {
            const coord = this.coordinates[0];
            features.push(new GeoJsonPoint(coord[1], coord[0], { editing: 'drawn' }));
        }
        else if (this.coordinates.length > 0) {
            features.push(new GeoJsonLineString(this.coordinates, {
                color: this.color,
                editing: 'drawn',
            }));
        }
        if (this.coordinates.length > 2) {
            features.push(new GeoJsonPolygon([[...this.coordinates, this.coordinates[0]]], {
                color: this.color,
                editing: 'fill',
            }));
        }
        return new GeoJsonFeatureCollection(features);
    }
    toGeoJson() {
        const properties = {
            color: this.color ? UserContentColorHex[this.color] : undefined,
            featureType: 'Geofence',
            name: this.name,
        };
        return new GeoJsonPolygon(this.feature.geometry.coordinates, properties, this.id);
    }
    validate() {
        if (this.polygon.length > 25) {
            return of('Point limit reached.');
        }
        if (this.polygon.length < 3) {
            return of('Not enough points.');
        }
        const firstPoint = this.feature.geometry.coordinates[0]?.[0];
        const foundKinks = kinks(this.feature.geometry);
        if (foundKinks.features.length === 1) {
            // find all intersecting points.
            if (isEqual(foundKinks.features[0]?.geometry.coordinates, firstPoint)) {
                return of('');
            }
            return of('Geofence may not cross itself.');
        }
        else if (foundKinks.features.length > 0) {
            return of('Geofence may not cross itself.');
        }
        return of('');
    }
}

class PlanRideButtonComponent {
    constructor(config) {
        this.config = config;
        this.showPlanRideIcon = false;
        this.fullWidth = true;
        this.startRidePlanningClicked = new EventEmitter();
        this.isRideTypeSelectorShowing = false;
    }
    closePlanARide(event) {
        if (!this.rideTypeSelector.nativeElement.contains(event.target)) {
            this.isRideTypeSelectorShowing = false;
        }
    }
    goToOrvRidePlanning() {
        this.planRideToHere('ORV');
    }
    goToSnowRidePlanning() {
        this.planRideToHere('SNO');
    }
    openPlanARide(event) {
        event.stopPropagation();
        if (this.config.isOffRoad) {
            this.isRideTypeSelectorShowing = !this.isRideTypeSelectorShowing;
        }
        else {
            this.planRideToHere('');
        }
    }
    planRideToHere(rideType) {
        this.startRidePlanningClicked.emit(rideType);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: PlanRideButtonComponent, deps: [{ token: i1.ConfigService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: PlanRideButtonComponent, isStandalone: true, selector: "pii-plan-ride-button", inputs: { showPlanRideIcon: "showPlanRideIcon", fullWidth: "fullWidth" }, outputs: { startRidePlanningClicked: "startRidePlanningClicked" }, host: { listeners: { "document:click": "closePlanARide($event)" } }, viewQueries: [{ propertyName: "rideTypeSelector", first: true, predicate: ["rideTypeSelector"], descendants: true }], ngImport: i0, template: "<div class=\"plan-ride-button-container\">\n  <div #rideTypeSelector class=\"ride-type-selector\" [hidden]=\"!isRideTypeSelectorShowing\">\n    <div class=\"ride-type-selector-open\">\n      <pii-button\n        text=\"Off-Road\"\n        elementId=\"offRoadRoute\"\n        align=\"left\"\n        variant=\"primary\"\n        iconName=\"orv\"\n        [fullWidth]=\"true\"\n        (click)=\"goToOrvRidePlanning()\"\n        i18n-text\n      />\n      <pii-button\n        text=\"Snowmobile\"\n        elementId=\"snowmobileRoute\"\n        variant=\"primary\"\n        iconName=\"snow\"\n        [fullWidth]=\"true\"\n        (click)=\"goToSnowRidePlanning()\"\n        i18n-text\n      />\n    </div>\n  </div>\n  <pii-button\n    text=\"Plan Ride\"\n    elementId=\"planARouteHere\"\n    iconClass=\"white\"\n    variant=\"primary\"\n    [fullWidth]=\"fullWidth\"\n    [iconName]=\"showPlanRideIcon ? 'routes' : ''\"\n    (clicked)=\"openPlanARide($event)\"\n    i18n-text\n  />\n</div>\n", styles: [".helper-bold{font-weight:700}.helper-full-width{width:100%}.helper-warning{color:red}.material-switch-input{display:none}.material-switch-label{cursor:pointer;display:inline-block;min-width:42px;position:relative;text-align:start}.material-switch-label:before,.material-switch-label:after{content:\"\";margin:0;outline:0;position:absolute;top:50%;transform:translateY(-50%);transition:all .3s ease}.material-switch-label:before{height:14px;width:34px;background-color:gray;border-radius:8px;left:1px}.material-switch-label:after{height:26px;width:26px;background-color:#d8d8d8;border-radius:50%;box-shadow:0 3px 1px -2px #00000024,0 2px 2px #00000019,0 1px 5px #00000015;left:0}.material-switch-label .toggle-on{display:none}.material-switch-label .toggle-off{display:inline-block}.material-switch-input:checked+.material-switch-label:before{background-color:var(--primary-color-lighter)}.material-switch-input:checked+.material-switch-label:after{background-color:var(--primary-color);transform:translate(80%,-50%)}.material-switch-input:checked+.material-switch-label .toggle-on{display:inline-block}.material-switch-input:checked+.material-switch-label .toggle-off{display:none}mat-expansion-panel-header{box-shadow:0 1px 3px 0 var(--shadow-color);height:54px}mat-expansion-panel-header.mat-expanded{height:54px}.mat-mdc-checkbox label{margin-bottom:0}.mat-mdc-checkbox input[disabled]{opacity:0}.mat-mdc-checkbox.multiline-checkbox .mdc-form-field{align-items:start}.mat-mdc-checkbox .mdc-checkbox .mdc-checkbox__native-control:enabled:not(:checked):not(:indeterminate):not([data-indeterminate=true])~.mdc-checkbox__background{border-color:#000!important}.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:not(:checked):not(:indeterminate):not([data-indeterminate=true])~.mdc-checkbox__background,.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:checked~.mdc-checkbox__background,.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:indeterminate~.mdc-checkbox__background,.mat-mdc-checkbox .mdc-checkbox__native-control[data-indeterminate=true][disabled]~.mdc-checkbox__background{background-color:transparent!important;border-color:#d8d8d8!important}.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:not(:checked):not(:indeterminate):not([data-indeterminate=true])~.mdc-checkbox__background svg,.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:checked~.mdc-checkbox__background svg,.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:indeterminate~.mdc-checkbox__background svg,.mat-mdc-checkbox .mdc-checkbox__native-control[data-indeterminate=true][disabled]~.mdc-checkbox__background svg{color:#d8d8d8!important}.mat-mdc-checkbox.mat-mdc-checkbox-disabled label{color:gray!important}.remove-bottom-ripple .mdc-text-field--filled .mdc-line-ripple:after{border-bottom:none}.remove-bottom-ripple.input-has-value .mat-mdc-form-field-infix{min-height:40px!important;padding:8px!important}.expanding-height-textarea .mdc-text-field--filled:not(.mdc-text-field--disabled){border:0!important;border-radius:0!important}.expanding-height-textarea .mat-mdc-form-field-subscript-wrapper{display:none}.mdc-text-field--filled:not(.mdc-text-field--disabled) .mdc-line-ripple:before{border-bottom-color:transparent!important}.mat-mdc-form-field-focus-overlay{background-color:#fff!important}.mat-mdc-form-field-infix{min-height:50px!important;padding-top:17px!important}.mdc-text-field--filled:not(.mdc-text-field--disabled){background-color:#fff!important;border:1px solid #d8d8d8;border-radius:5px}.mdc-text-field--filled:not(.mdc-text-field--disabled).mdc-text-field--invalid{border-color:#d0021b}.mdc-floating-label{color:gray!important}.mdc-text-field--invalid.mdc-text-field:not(.mdc-text-field--disabled) .mdc-text-field__input{color:#d0021b!important}.mat-mdc-form-field-subscript-wrapper{bottom:18px;height:20px!important;left:-5px;position:relative}.hide-input-error .mat-mdc-form-field-subscript-wrapper{height:0!important}.mat-mdc-form-field-hint-wrapper,.mat-mdc-form-field-error-wrapper{left:-5px!important;top:15px!important}.mat-mdc-floating-label{top:25px!important}.mat-mdc-form-field input[type=color],.mat-mdc-form-field input[type=date],.mat-mdc-form-field input[type=datetime-local],.mat-mdc-form-field input[type=datetime],.mat-mdc-form-field input[type=email],.mat-mdc-form-field input[type=month],.mat-mdc-form-field input[type=number],.mat-mdc-form-field input[type=password],.mat-mdc-form-field input[type=search],.mat-mdc-form-field input[type=tel],.mat-mdc-form-field input[type=text],.mat-mdc-form-field input[type=time],.mat-mdc-form-field input[type=url],.mat-mdc-form-field input[type=week]{border:initial!important;height:initial!important;padding:initial!important}.mat-mdc-form-field.inherit{display:inherit!important}.responsive-img{display:block;height:100%;max-width:100%;width:100%}.responsive-img-cover{height:100%;object-fit:cover;width:100%}.responsive-img-contain{height:auto;object-fit:contain;width:100%}.display-flex-container{display:flex}.flex-1-auto{flex:1 1 auto}.flex-1{flex:1}.flex-2{flex:2}.flex-3{flex:3}.list-unstyled,.list-inline{list-style:none;margin:0;padding:0}.list-inline>li{display:inline-block;padding:0}.default-color,.profile-blue{background:#09c}.profile-darkblue,.profile-dark-blue{background:#05f}.profile-brown{background:#7f4f04}.profile-gold{background:#fc0}.profile-green{background:#26c407}.profile-darkgreen{background:#090}.profile-orange{background:#ff7e00}.profile-purple{background:#90c}.profile-red{background:#c00}.profile-salmon{background:#f66}.primary-bg{background:var(--primary-color)}*{box-sizing:border-box}body{font-size:1rem;line-height:1.4rem}a{color:var(--primary-color);text-decoration:none}h1{font-size:2.25rem;line-height:2.25rem;font-family:var(--brand-font);margin:0}h1.small-h1{font-size:1.1rem;line-height:1.1rem;font-family:Helvetica,serif}.story-wrapper{background-color:#ffffffd9;border-radius:10px;box-shadow:0 2px 8px #57192133;font-family:Helvetica,serif;padding:24px;width:max-content}.story-wrapper.narrow{max-width:300px}.story-wrapper.zero-padding{padding:0}.story-wrapper .small-padding{padding:0 4px}.story-wrapper .medium-padding{padding:0 8px}.story-wrapper .large-padding{padding:0 16px}.story-wrapper .x-large-padding{padding:0 24px}.story-wrapper .xx-large-padding{padding:0 32px}.plan-ride-button-container{position:relative}.plan-ride-button-container svg{height:24pxpx;width:24pxpx;fill:#fff}.plan-ride-button-container .button{font-size:1.1rem;line-height:1.1rem;align-items:center;background-color:var(--primary-color);border:0;border-radius:5px;box-shadow:0 1px 3px 0 var(--shadow-color);color:#fff;cursor:pointer;font-weight:600;height:45px;justify-content:center;min-width:140px;outline:none;padding:10px 15px;position:relative;text-decoration:none;transition:box-shadow .4s cubic-bezier(.25,.8,.25,1),background-color .4s cubic-bezier(.25,.8,.25,1);-webkit-user-select:none;user-select:none;vertical-align:middle;white-space:nowrap}.plan-ride-button-container .button.full-width{width:100%}.plan-ride-button-container .button.plan-ride-button-icon{display:grid;grid-gap:10px;grid-template-columns:auto auto}.plan-ride-button-container .ride-type-selector-open{background:#fff;border-radius:10px;bottom:-4px;display:grid;filter:drop-shadow(-2px 2px 2px var(--shadow-color));gap:10px;left:50%;padding:10px;position:absolute;transform:translate(-50%,-50%);width:max-content;z-index:1}.plan-ride-button-container .ride-type-selector-open:after{border-color:white transparent;border-style:solid;border-width:10px 12px 0;bottom:-10px;content:\"\";display:block;left:50%;margin-left:-12px;position:absolute;width:0;z-index:1}.plan-ride-button-container .ride-type-selector-open .button{display:grid;grid-gap:10px;grid-template-columns:auto 1fr;text-align:start}.plan-ride-button-container .ride-type-selector-open .button:first-child{border-bottom:none}\n"], dependencies: [{ kind: "ngmodule", type: MatIconModule }, { kind: "component", type: ButtonComponent, selector: "pii-button", inputs: ["align", "disabled", "elementId", "expanded", "fullHeight", "fullWidth", "iconClass", "iconName", "maxWidth", "text", "textColor", "variant"], outputs: ["clicked"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: PlanRideButtonComponent, decorators: [{
            type: Component,
            args: [{ imports: [MatIconModule, ButtonComponent], selector: 'pii-plan-ride-button', standalone: true, template: "<div class=\"plan-ride-button-container\">\n  <div #rideTypeSelector class=\"ride-type-selector\" [hidden]=\"!isRideTypeSelectorShowing\">\n    <div class=\"ride-type-selector-open\">\n      <pii-button\n        text=\"Off-Road\"\n        elementId=\"offRoadRoute\"\n        align=\"left\"\n        variant=\"primary\"\n        iconName=\"orv\"\n        [fullWidth]=\"true\"\n        (click)=\"goToOrvRidePlanning()\"\n        i18n-text\n      />\n      <pii-button\n        text=\"Snowmobile\"\n        elementId=\"snowmobileRoute\"\n        variant=\"primary\"\n        iconName=\"snow\"\n        [fullWidth]=\"true\"\n        (click)=\"goToSnowRidePlanning()\"\n        i18n-text\n      />\n    </div>\n  </div>\n  <pii-button\n    text=\"Plan Ride\"\n    elementId=\"planARouteHere\"\n    iconClass=\"white\"\n    variant=\"primary\"\n    [fullWidth]=\"fullWidth\"\n    [iconName]=\"showPlanRideIcon ? 'routes' : ''\"\n    (clicked)=\"openPlanARide($event)\"\n    i18n-text\n  />\n</div>\n", styles: [".helper-bold{font-weight:700}.helper-full-width{width:100%}.helper-warning{color:red}.material-switch-input{display:none}.material-switch-label{cursor:pointer;display:inline-block;min-width:42px;position:relative;text-align:start}.material-switch-label:before,.material-switch-label:after{content:\"\";margin:0;outline:0;position:absolute;top:50%;transform:translateY(-50%);transition:all .3s ease}.material-switch-label:before{height:14px;width:34px;background-color:gray;border-radius:8px;left:1px}.material-switch-label:after{height:26px;width:26px;background-color:#d8d8d8;border-radius:50%;box-shadow:0 3px 1px -2px #00000024,0 2px 2px #00000019,0 1px 5px #00000015;left:0}.material-switch-label .toggle-on{display:none}.material-switch-label .toggle-off{display:inline-block}.material-switch-input:checked+.material-switch-label:before{background-color:var(--primary-color-lighter)}.material-switch-input:checked+.material-switch-label:after{background-color:var(--primary-color);transform:translate(80%,-50%)}.material-switch-input:checked+.material-switch-label .toggle-on{display:inline-block}.material-switch-input:checked+.material-switch-label .toggle-off{display:none}mat-expansion-panel-header{box-shadow:0 1px 3px 0 var(--shadow-color);height:54px}mat-expansion-panel-header.mat-expanded{height:54px}.mat-mdc-checkbox label{margin-bottom:0}.mat-mdc-checkbox input[disabled]{opacity:0}.mat-mdc-checkbox.multiline-checkbox .mdc-form-field{align-items:start}.mat-mdc-checkbox .mdc-checkbox .mdc-checkbox__native-control:enabled:not(:checked):not(:indeterminate):not([data-indeterminate=true])~.mdc-checkbox__background{border-color:#000!important}.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:not(:checked):not(:indeterminate):not([data-indeterminate=true])~.mdc-checkbox__background,.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:checked~.mdc-checkbox__background,.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:indeterminate~.mdc-checkbox__background,.mat-mdc-checkbox .mdc-checkbox__native-control[data-indeterminate=true][disabled]~.mdc-checkbox__background{background-color:transparent!important;border-color:#d8d8d8!important}.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:not(:checked):not(:indeterminate):not([data-indeterminate=true])~.mdc-checkbox__background svg,.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:checked~.mdc-checkbox__background svg,.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:indeterminate~.mdc-checkbox__background svg,.mat-mdc-checkbox .mdc-checkbox__native-control[data-indeterminate=true][disabled]~.mdc-checkbox__background svg{color:#d8d8d8!important}.mat-mdc-checkbox.mat-mdc-checkbox-disabled label{color:gray!important}.remove-bottom-ripple .mdc-text-field--filled .mdc-line-ripple:after{border-bottom:none}.remove-bottom-ripple.input-has-value .mat-mdc-form-field-infix{min-height:40px!important;padding:8px!important}.expanding-height-textarea .mdc-text-field--filled:not(.mdc-text-field--disabled){border:0!important;border-radius:0!important}.expanding-height-textarea .mat-mdc-form-field-subscript-wrapper{display:none}.mdc-text-field--filled:not(.mdc-text-field--disabled) .mdc-line-ripple:before{border-bottom-color:transparent!important}.mat-mdc-form-field-focus-overlay{background-color:#fff!important}.mat-mdc-form-field-infix{min-height:50px!important;padding-top:17px!important}.mdc-text-field--filled:not(.mdc-text-field--disabled){background-color:#fff!important;border:1px solid #d8d8d8;border-radius:5px}.mdc-text-field--filled:not(.mdc-text-field--disabled).mdc-text-field--invalid{border-color:#d0021b}.mdc-floating-label{color:gray!important}.mdc-text-field--invalid.mdc-text-field:not(.mdc-text-field--disabled) .mdc-text-field__input{color:#d0021b!important}.mat-mdc-form-field-subscript-wrapper{bottom:18px;height:20px!important;left:-5px;position:relative}.hide-input-error .mat-mdc-form-field-subscript-wrapper{height:0!important}.mat-mdc-form-field-hint-wrapper,.mat-mdc-form-field-error-wrapper{left:-5px!important;top:15px!important}.mat-mdc-floating-label{top:25px!important}.mat-mdc-form-field input[type=color],.mat-mdc-form-field input[type=date],.mat-mdc-form-field input[type=datetime-local],.mat-mdc-form-field input[type=datetime],.mat-mdc-form-field input[type=email],.mat-mdc-form-field input[type=month],.mat-mdc-form-field input[type=number],.mat-mdc-form-field input[type=password],.mat-mdc-form-field input[type=search],.mat-mdc-form-field input[type=tel],.mat-mdc-form-field input[type=text],.mat-mdc-form-field input[type=time],.mat-mdc-form-field input[type=url],.mat-mdc-form-field input[type=week]{border:initial!important;height:initial!important;padding:initial!important}.mat-mdc-form-field.inherit{display:inherit!important}.responsive-img{display:block;height:100%;max-width:100%;width:100%}.responsive-img-cover{height:100%;object-fit:cover;width:100%}.responsive-img-contain{height:auto;object-fit:contain;width:100%}.display-flex-container{display:flex}.flex-1-auto{flex:1 1 auto}.flex-1{flex:1}.flex-2{flex:2}.flex-3{flex:3}.list-unstyled,.list-inline{list-style:none;margin:0;padding:0}.list-inline>li{display:inline-block;padding:0}.default-color,.profile-blue{background:#09c}.profile-darkblue,.profile-dark-blue{background:#05f}.profile-brown{background:#7f4f04}.profile-gold{background:#fc0}.profile-green{background:#26c407}.profile-darkgreen{background:#090}.profile-orange{background:#ff7e00}.profile-purple{background:#90c}.profile-red{background:#c00}.profile-salmon{background:#f66}.primary-bg{background:var(--primary-color)}*{box-sizing:border-box}body{font-size:1rem;line-height:1.4rem}a{color:var(--primary-color);text-decoration:none}h1{font-size:2.25rem;line-height:2.25rem;font-family:var(--brand-font);margin:0}h1.small-h1{font-size:1.1rem;line-height:1.1rem;font-family:Helvetica,serif}.story-wrapper{background-color:#ffffffd9;border-radius:10px;box-shadow:0 2px 8px #57192133;font-family:Helvetica,serif;padding:24px;width:max-content}.story-wrapper.narrow{max-width:300px}.story-wrapper.zero-padding{padding:0}.story-wrapper .small-padding{padding:0 4px}.story-wrapper .medium-padding{padding:0 8px}.story-wrapper .large-padding{padding:0 16px}.story-wrapper .x-large-padding{padding:0 24px}.story-wrapper .xx-large-padding{padding:0 32px}.plan-ride-button-container{position:relative}.plan-ride-button-container svg{height:24pxpx;width:24pxpx;fill:#fff}.plan-ride-button-container .button{font-size:1.1rem;line-height:1.1rem;align-items:center;background-color:var(--primary-color);border:0;border-radius:5px;box-shadow:0 1px 3px 0 var(--shadow-color);color:#fff;cursor:pointer;font-weight:600;height:45px;justify-content:center;min-width:140px;outline:none;padding:10px 15px;position:relative;text-decoration:none;transition:box-shadow .4s cubic-bezier(.25,.8,.25,1),background-color .4s cubic-bezier(.25,.8,.25,1);-webkit-user-select:none;user-select:none;vertical-align:middle;white-space:nowrap}.plan-ride-button-container .button.full-width{width:100%}.plan-ride-button-container .button.plan-ride-button-icon{display:grid;grid-gap:10px;grid-template-columns:auto auto}.plan-ride-button-container .ride-type-selector-open{background:#fff;border-radius:10px;bottom:-4px;display:grid;filter:drop-shadow(-2px 2px 2px var(--shadow-color));gap:10px;left:50%;padding:10px;position:absolute;transform:translate(-50%,-50%);width:max-content;z-index:1}.plan-ride-button-container .ride-type-selector-open:after{border-color:white transparent;border-style:solid;border-width:10px 12px 0;bottom:-10px;content:\"\";display:block;left:50%;margin-left:-12px;position:absolute;width:0;z-index:1}.plan-ride-button-container .ride-type-selector-open .button{display:grid;grid-gap:10px;grid-template-columns:auto 1fr;text-align:start}.plan-ride-button-container .ride-type-selector-open .button:first-child{border-bottom:none}\n"] }]
        }], ctorParameters: () => [{ type: i1.ConfigService }], propDecorators: { showPlanRideIcon: [{
                type: Input
            }], fullWidth: [{
                type: Input
            }], startRidePlanningClicked: [{
                type: Output
            }], rideTypeSelector: [{
                type: ViewChild,
                args: ['rideTypeSelector']
            }], closePlanARide: [{
                type: HostListener,
                args: ['document:click', ['$event']]
            }] } });

class ShareItem {
    constructor(contract = {}) {
        this.contract = { ...contract };
    }
    get entityType() {
        return this.contract.entityType || 'Unknown';
    }
    get id() {
        return this.contract.id || '';
    }
    toContract() {
        return cloneDeep(this.contract);
    }
}

class Share {
    constructor(contract = {}) {
        this.contract = cloneDeep(contract);
    }
    get appSku() {
        return this.contract.appSku || '';
    }
    get creationDate() {
        return this.contract.creationDate ? new Date(this.contract.creationDate) : undefined;
    }
    get deepLink() {
        return this.contract.deepLink || '';
    }
    get id() {
        return this.contract.id || '';
    }
    get isActive() {
        return Boolean(this.contract.isActive);
    }
    get riderId() {
        return this.contract.riderId || '';
    }
    get sharedByName() {
        return this.contract.sharedByName || '';
    }
    get sharedItems() {
        return this.contract.sharedItems?.map((s) => new ShareItem(s)) || [];
    }
    get snapshots() {
        return this.contract.snapshots || [];
    }
    get title() {
        return this.contract.title || '';
    }
}

class ShareItemCopyResponse {
    constructor(contract) {
        this.contract = contract;
    }
    get copyId() {
        return this.contract.copyId || '';
    }
    get entityType() {
        return this.contract.entityType || 'Unknown';
    }
    get originalId() {
        return this.contract.originalId || '';
    }
    get wasCopied() {
        return this.contract.wasCopied || false;
    }
}

class ShareResourceService {
    constructor(configService, http, routingHelpersService) {
        this.http = http;
        this.routingHelpersService = routingHelpersService;
        this.baseUrl = routingHelpersService.combineUrl(configService.apiUrl.rideCommand, 'shares');
    }
    getShareById(shareId) {
        return this.http.get(this.routingHelpersService.combineUrl(this.baseUrl, shareId), {
            params: new HttpParams().set('skipAuthorization', 'true'),
        });
    }
    copyToAccount(shareId, shareItems) {
        return this.http.post(this.routingHelpersService.combineUrl(this.baseUrl, shareId, 'copy'), shareItems);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ShareResourceService, deps: [{ token: i1.ConfigService }, { token: i3.WebComponentHttpClient }, { token: i2.RoutingHelpersService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ShareResourceService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ShareResourceService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1.ConfigService }, { type: i3.WebComponentHttpClient }, { type: i2.RoutingHelpersService }] });

class ShareService {
    constructor(resource) {
        this.resource = resource;
        this.shareCacheCollection = new CacheCollectionObservable();
        this.shareSignal = signal(undefined);
        this.$share = this.shareSignal.asReadonly();
    }
    setActiveShareId(shareId) {
        if (shareId) {
            this.shareCacheCollection
                .getItemWithGetter(this.resource.getShareById(shareId).pipe(map((contract) => new Share(contract))), shareId)
                .subscribe((res) => {
                this.shareSignal.set(res);
            });
        }
        else {
            this.shareSignal.set(undefined);
        }
    }
    copyToAccount(shareId, shareItems) {
        return this.resource
            .copyToAccount(shareId, shareItems.map((x) => x.toContract()))
            .pipe(map((res) => res.map((item) => new ShareItemCopyResponse(item))));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ShareService, deps: [{ token: ShareResourceService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ShareService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ShareService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: ShareResourceService }] });

class SaveShareButtonComponent {
    constructor(shareService, userProfileService) {
        this.shareService = shareService;
        this.$expanded = input(false);
        this.saved = output();
        this.saveText = $localize `Save`;
        const $riderId = toSignal(userProfileService.getUserRiderId$());
        this.$share = shareService.$share;
        this.$showButton = computed(() => Boolean(this.$share()?.riderId) && this.$share()?.riderId !== $riderId());
    }
    save() {
        this.shareService.copyToAccount(this.$share()?.id || '', this.$share()?.sharedItems || []).subscribe((res) => {
            this.saved.emit({ id: res[0]?.copyId || '', type: res[0]?.entityType ?? 'Unknown' });
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: SaveShareButtonComponent, deps: [{ token: ShareService }, { token: i2$1.UserProfileService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.14", type: SaveShareButtonComponent, isStandalone: true, selector: "pii-save-share-button", inputs: { $expanded: { classPropertyName: "$expanded", publicName: "$expanded", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { saved: "saved" }, ngImport: i0, template: "<div class=\"save-share-button-container\">\n  @if ($showButton()) {\n    <pii-button\n      elementId=\"saveShare\"\n      variant=\"primary\"\n      [expanded]=\"$expanded()\"\n      [text]=\"saveText\"\n      [fullWidth]=\"true\"\n      (clicked)=\"save()\"\n    />\n  }\n</div>\n", styles: [""], dependencies: [{ kind: "component", type: ButtonComponent, selector: "pii-button", inputs: ["align", "disabled", "elementId", "expanded", "fullHeight", "fullWidth", "iconClass", "iconName", "maxWidth", "text", "textColor", "variant"], outputs: ["clicked"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: SaveShareButtonComponent, decorators: [{
            type: Component,
            args: [{ imports: [ButtonComponent], selector: 'pii-save-share-button', standalone: true, template: "<div class=\"save-share-button-container\">\n  @if ($showButton()) {\n    <pii-button\n      elementId=\"saveShare\"\n      variant=\"primary\"\n      [expanded]=\"$expanded()\"\n      [text]=\"saveText\"\n      [fullWidth]=\"true\"\n      (clicked)=\"save()\"\n    />\n  }\n</div>\n" }]
        }], ctorParameters: () => [{ type: ShareService }, { type: i2$1.UserProfileService }] });

class MapDetailBottomButtonBarComponent {
    constructor(shareService) {
        this.$canAddToPlannedRide = input(false);
        this.$canStartPlanningRide = input(false);
        this.shareSaved = output();
        this.planRideClicked = output();
        this.$hasShare = computed(() => Boolean(shareService.$share()));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: MapDetailBottomButtonBarComponent, deps: [{ token: ShareService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.14", type: MapDetailBottomButtonBarComponent, isStandalone: true, selector: "pii-map-detail-bottom-button-bar", inputs: { $canAddToPlannedRide: { classPropertyName: "$canAddToPlannedRide", publicName: "$canAddToPlannedRide", isSignal: true, isRequired: false, transformFunction: null }, $canStartPlanningRide: { classPropertyName: "$canStartPlanningRide", publicName: "$canStartPlanningRide", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { shareSaved: "shareSaved", planRideClicked: "planRideClicked" }, ngImport: i0, template: "<div class=\"map-detail-bottom-buttons-container\">\n  <div [ngClass]=\"{ padding: $hasShare() || $canAddToPlannedRide() || $canStartPlanningRide() }\">\n    @if ($hasShare()) {\n      <pii-save-share-button (saved)=\"shareSaved.emit($event)\" />\n    } @else if ($canAddToPlannedRide()) {\n      <!-- TODO: Refactor and Implement -->\n      <!-- <rdx-planned-ride-stop\n        [mapPoint]=\"mapPoint\"\n        [popupMode]=\"false\"\n        (addMapPointToPlannedRide)=\"addPointToPlannedRide(mapPoint)\"\n        (removeMapPointFromPlannedRide)=\"removePointFromPlannedRide($event.mapPoint)\"\n      /> -->\n    } @else if ($canStartPlanningRide()) {\n      <pii-plan-ride-button [showPlanRideIcon]=\"true\" (startRidePlanningClicked)=\"planRideClicked.emit($event)\" />\n    }\n  </div>\n</div>\n", styles: [".helper-bold{font-weight:700}.helper-full-width{width:100%}.helper-warning{color:red}.material-switch-input{display:none}.material-switch-label{cursor:pointer;display:inline-block;min-width:42px;position:relative;text-align:start}.material-switch-label:before,.material-switch-label:after{content:\"\";margin:0;outline:0;position:absolute;top:50%;transform:translateY(-50%);transition:all .3s ease}.material-switch-label:before{height:14px;width:34px;background-color:gray;border-radius:8px;left:1px}.material-switch-label:after{height:26px;width:26px;background-color:#d8d8d8;border-radius:50%;box-shadow:0 3px 1px -2px #00000024,0 2px 2px #00000019,0 1px 5px #00000015;left:0}.material-switch-label .toggle-on{display:none}.material-switch-label .toggle-off{display:inline-block}.material-switch-input:checked+.material-switch-label:before{background-color:var(--primary-color-lighter)}.material-switch-input:checked+.material-switch-label:after{background-color:var(--primary-color);transform:translate(80%,-50%)}.material-switch-input:checked+.material-switch-label .toggle-on{display:inline-block}.material-switch-input:checked+.material-switch-label .toggle-off{display:none}mat-expansion-panel-header{box-shadow:0 1px 3px 0 var(--shadow-color);height:54px}mat-expansion-panel-header.mat-expanded{height:54px}.mat-mdc-checkbox label{margin-bottom:0}.mat-mdc-checkbox input[disabled]{opacity:0}.mat-mdc-checkbox.multiline-checkbox .mdc-form-field{align-items:start}.mat-mdc-checkbox .mdc-checkbox .mdc-checkbox__native-control:enabled:not(:checked):not(:indeterminate):not([data-indeterminate=true])~.mdc-checkbox__background{border-color:#000!important}.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:not(:checked):not(:indeterminate):not([data-indeterminate=true])~.mdc-checkbox__background,.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:checked~.mdc-checkbox__background,.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:indeterminate~.mdc-checkbox__background,.mat-mdc-checkbox .mdc-checkbox__native-control[data-indeterminate=true][disabled]~.mdc-checkbox__background{background-color:transparent!important;border-color:#d8d8d8!important}.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:not(:checked):not(:indeterminate):not([data-indeterminate=true])~.mdc-checkbox__background svg,.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:checked~.mdc-checkbox__background svg,.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:indeterminate~.mdc-checkbox__background svg,.mat-mdc-checkbox .mdc-checkbox__native-control[data-indeterminate=true][disabled]~.mdc-checkbox__background svg{color:#d8d8d8!important}.mat-mdc-checkbox.mat-mdc-checkbox-disabled label{color:gray!important}.remove-bottom-ripple .mdc-text-field--filled .mdc-line-ripple:after{border-bottom:none}.remove-bottom-ripple.input-has-value .mat-mdc-form-field-infix{min-height:40px!important;padding:8px!important}.expanding-height-textarea .mdc-text-field--filled:not(.mdc-text-field--disabled){border:0!important;border-radius:0!important}.expanding-height-textarea .mat-mdc-form-field-subscript-wrapper{display:none}.mdc-text-field--filled:not(.mdc-text-field--disabled) .mdc-line-ripple:before{border-bottom-color:transparent!important}.mat-mdc-form-field-focus-overlay{background-color:#fff!important}.mat-mdc-form-field-infix{min-height:50px!important;padding-top:17px!important}.mdc-text-field--filled:not(.mdc-text-field--disabled){background-color:#fff!important;border:1px solid #d8d8d8;border-radius:5px}.mdc-text-field--filled:not(.mdc-text-field--disabled).mdc-text-field--invalid{border-color:#d0021b}.mdc-floating-label{color:gray!important}.mdc-text-field--invalid.mdc-text-field:not(.mdc-text-field--disabled) .mdc-text-field__input{color:#d0021b!important}.mat-mdc-form-field-subscript-wrapper{bottom:18px;height:20px!important;left:-5px;position:relative}.hide-input-error .mat-mdc-form-field-subscript-wrapper{height:0!important}.mat-mdc-form-field-hint-wrapper,.mat-mdc-form-field-error-wrapper{left:-5px!important;top:15px!important}.mat-mdc-floating-label{top:25px!important}.mat-mdc-form-field input[type=color],.mat-mdc-form-field input[type=date],.mat-mdc-form-field input[type=datetime-local],.mat-mdc-form-field input[type=datetime],.mat-mdc-form-field input[type=email],.mat-mdc-form-field input[type=month],.mat-mdc-form-field input[type=number],.mat-mdc-form-field input[type=password],.mat-mdc-form-field input[type=search],.mat-mdc-form-field input[type=tel],.mat-mdc-form-field input[type=text],.mat-mdc-form-field input[type=time],.mat-mdc-form-field input[type=url],.mat-mdc-form-field input[type=week]{border:initial!important;height:initial!important;padding:initial!important}.mat-mdc-form-field.inherit{display:inherit!important}.responsive-img{display:block;height:100%;max-width:100%;width:100%}.responsive-img-cover{height:100%;object-fit:cover;width:100%}.responsive-img-contain{height:auto;object-fit:contain;width:100%}.display-flex-container{display:flex}.flex-1-auto{flex:1 1 auto}.flex-1{flex:1}.flex-2{flex:2}.flex-3{flex:3}.list-unstyled,.list-inline{list-style:none;margin:0;padding:0}.list-inline>li{display:inline-block;padding:0}.default-color,.profile-blue{background:#09c}.profile-darkblue,.profile-dark-blue{background:#05f}.profile-brown{background:#7f4f04}.profile-gold{background:#fc0}.profile-green{background:#26c407}.profile-darkgreen{background:#090}.profile-orange{background:#ff7e00}.profile-purple{background:#90c}.profile-red{background:#c00}.profile-salmon{background:#f66}.primary-bg{background:var(--primary-color)}*{box-sizing:border-box}body{font-size:1rem;line-height:1.4rem}a{color:var(--primary-color);text-decoration:none}h1{font-size:2.25rem;line-height:2.25rem;font-family:var(--brand-font);margin:0}h1.small-h1{font-size:1.1rem;line-height:1.1rem;font-family:Helvetica,serif}.story-wrapper{background-color:#ffffffd9;border-radius:10px;box-shadow:0 2px 8px #57192133;font-family:Helvetica,serif;padding:24px;width:max-content}.story-wrapper.narrow{max-width:300px}.story-wrapper.zero-padding{padding:0}.story-wrapper .small-padding{padding:0 4px}.story-wrapper .medium-padding{padding:0 8px}.story-wrapper .large-padding{padding:0 16px}.story-wrapper .x-large-padding{padding:0 24px}.story-wrapper .xx-large-padding{padding:0 32px}.padding{padding:16px}\n"], dependencies: [{ kind: "component", type: PlanRideButtonComponent, selector: "pii-plan-ride-button", inputs: ["showPlanRideIcon", "fullWidth"], outputs: ["startRidePlanningClicked"] }, { kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "component", type: SaveShareButtonComponent, selector: "pii-save-share-button", inputs: ["$expanded"], outputs: ["saved"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: MapDetailBottomButtonBarComponent, decorators: [{
            type: Component,
            args: [{ imports: [PlanRideButtonComponent, NgClass, SaveShareButtonComponent], selector: 'pii-map-detail-bottom-button-bar', standalone: true, template: "<div class=\"map-detail-bottom-buttons-container\">\n  <div [ngClass]=\"{ padding: $hasShare() || $canAddToPlannedRide() || $canStartPlanningRide() }\">\n    @if ($hasShare()) {\n      <pii-save-share-button (saved)=\"shareSaved.emit($event)\" />\n    } @else if ($canAddToPlannedRide()) {\n      <!-- TODO: Refactor and Implement -->\n      <!-- <rdx-planned-ride-stop\n        [mapPoint]=\"mapPoint\"\n        [popupMode]=\"false\"\n        (addMapPointToPlannedRide)=\"addPointToPlannedRide(mapPoint)\"\n        (removeMapPointFromPlannedRide)=\"removePointFromPlannedRide($event.mapPoint)\"\n      /> -->\n    } @else if ($canStartPlanningRide()) {\n      <pii-plan-ride-button [showPlanRideIcon]=\"true\" (startRidePlanningClicked)=\"planRideClicked.emit($event)\" />\n    }\n  </div>\n</div>\n", styles: [".helper-bold{font-weight:700}.helper-full-width{width:100%}.helper-warning{color:red}.material-switch-input{display:none}.material-switch-label{cursor:pointer;display:inline-block;min-width:42px;position:relative;text-align:start}.material-switch-label:before,.material-switch-label:after{content:\"\";margin:0;outline:0;position:absolute;top:50%;transform:translateY(-50%);transition:all .3s ease}.material-switch-label:before{height:14px;width:34px;background-color:gray;border-radius:8px;left:1px}.material-switch-label:after{height:26px;width:26px;background-color:#d8d8d8;border-radius:50%;box-shadow:0 3px 1px -2px #00000024,0 2px 2px #00000019,0 1px 5px #00000015;left:0}.material-switch-label .toggle-on{display:none}.material-switch-label .toggle-off{display:inline-block}.material-switch-input:checked+.material-switch-label:before{background-color:var(--primary-color-lighter)}.material-switch-input:checked+.material-switch-label:after{background-color:var(--primary-color);transform:translate(80%,-50%)}.material-switch-input:checked+.material-switch-label .toggle-on{display:inline-block}.material-switch-input:checked+.material-switch-label .toggle-off{display:none}mat-expansion-panel-header{box-shadow:0 1px 3px 0 var(--shadow-color);height:54px}mat-expansion-panel-header.mat-expanded{height:54px}.mat-mdc-checkbox label{margin-bottom:0}.mat-mdc-checkbox input[disabled]{opacity:0}.mat-mdc-checkbox.multiline-checkbox .mdc-form-field{align-items:start}.mat-mdc-checkbox .mdc-checkbox .mdc-checkbox__native-control:enabled:not(:checked):not(:indeterminate):not([data-indeterminate=true])~.mdc-checkbox__background{border-color:#000!important}.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:not(:checked):not(:indeterminate):not([data-indeterminate=true])~.mdc-checkbox__background,.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:checked~.mdc-checkbox__background,.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:indeterminate~.mdc-checkbox__background,.mat-mdc-checkbox .mdc-checkbox__native-control[data-indeterminate=true][disabled]~.mdc-checkbox__background{background-color:transparent!important;border-color:#d8d8d8!important}.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:not(:checked):not(:indeterminate):not([data-indeterminate=true])~.mdc-checkbox__background svg,.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:checked~.mdc-checkbox__background svg,.mat-mdc-checkbox .mdc-checkbox__native-control[disabled]:indeterminate~.mdc-checkbox__background svg,.mat-mdc-checkbox .mdc-checkbox__native-control[data-indeterminate=true][disabled]~.mdc-checkbox__background svg{color:#d8d8d8!important}.mat-mdc-checkbox.mat-mdc-checkbox-disabled label{color:gray!important}.remove-bottom-ripple .mdc-text-field--filled .mdc-line-ripple:after{border-bottom:none}.remove-bottom-ripple.input-has-value .mat-mdc-form-field-infix{min-height:40px!important;padding:8px!important}.expanding-height-textarea .mdc-text-field--filled:not(.mdc-text-field--disabled){border:0!important;border-radius:0!important}.expanding-height-textarea .mat-mdc-form-field-subscript-wrapper{display:none}.mdc-text-field--filled:not(.mdc-text-field--disabled) .mdc-line-ripple:before{border-bottom-color:transparent!important}.mat-mdc-form-field-focus-overlay{background-color:#fff!important}.mat-mdc-form-field-infix{min-height:50px!important;padding-top:17px!important}.mdc-text-field--filled:not(.mdc-text-field--disabled){background-color:#fff!important;border:1px solid #d8d8d8;border-radius:5px}.mdc-text-field--filled:not(.mdc-text-field--disabled).mdc-text-field--invalid{border-color:#d0021b}.mdc-floating-label{color:gray!important}.mdc-text-field--invalid.mdc-text-field:not(.mdc-text-field--disabled) .mdc-text-field__input{color:#d0021b!important}.mat-mdc-form-field-subscript-wrapper{bottom:18px;height:20px!important;left:-5px;position:relative}.hide-input-error .mat-mdc-form-field-subscript-wrapper{height:0!important}.mat-mdc-form-field-hint-wrapper,.mat-mdc-form-field-error-wrapper{left:-5px!important;top:15px!important}.mat-mdc-floating-label{top:25px!important}.mat-mdc-form-field input[type=color],.mat-mdc-form-field input[type=date],.mat-mdc-form-field input[type=datetime-local],.mat-mdc-form-field input[type=datetime],.mat-mdc-form-field input[type=email],.mat-mdc-form-field input[type=month],.mat-mdc-form-field input[type=number],.mat-mdc-form-field input[type=password],.mat-mdc-form-field input[type=search],.mat-mdc-form-field input[type=tel],.mat-mdc-form-field input[type=text],.mat-mdc-form-field input[type=time],.mat-mdc-form-field input[type=url],.mat-mdc-form-field input[type=week]{border:initial!important;height:initial!important;padding:initial!important}.mat-mdc-form-field.inherit{display:inherit!important}.responsive-img{display:block;height:100%;max-width:100%;width:100%}.responsive-img-cover{height:100%;object-fit:cover;width:100%}.responsive-img-contain{height:auto;object-fit:contain;width:100%}.display-flex-container{display:flex}.flex-1-auto{flex:1 1 auto}.flex-1{flex:1}.flex-2{flex:2}.flex-3{flex:3}.list-unstyled,.list-inline{list-style:none;margin:0;padding:0}.list-inline>li{display:inline-block;padding:0}.default-color,.profile-blue{background:#09c}.profile-darkblue,.profile-dark-blue{background:#05f}.profile-brown{background:#7f4f04}.profile-gold{background:#fc0}.profile-green{background:#26c407}.profile-darkgreen{background:#090}.profile-orange{background:#ff7e00}.profile-purple{background:#90c}.profile-red{background:#c00}.profile-salmon{background:#f66}.primary-bg{background:var(--primary-color)}*{box-sizing:border-box}body{font-size:1rem;line-height:1.4rem}a{color:var(--primary-color);text-decoration:none}h1{font-size:2.25rem;line-height:2.25rem;font-family:var(--brand-font);margin:0}h1.small-h1{font-size:1.1rem;line-height:1.1rem;font-family:Helvetica,serif}.story-wrapper{background-color:#ffffffd9;border-radius:10px;box-shadow:0 2px 8px #57192133;font-family:Helvetica,serif;padding:24px;width:max-content}.story-wrapper.narrow{max-width:300px}.story-wrapper.zero-padding{padding:0}.story-wrapper .small-padding{padding:0 4px}.story-wrapper .medium-padding{padding:0 8px}.story-wrapper .large-padding{padding:0 16px}.story-wrapper .x-large-padding{padding:0 24px}.story-wrapper .xx-large-padding{padding:0 32px}.padding{padding:16px}\n"] }]
        }], ctorParameters: () => [{ type: ShareService }] });

class PlannedRideRouteSegment {
    constructor(contract = {}) {
        this._plannedRideId = '';
        this.contract = contract;
        if (!this.properties.segmentId) {
            this.properties.segmentId = UuidService.generate();
        }
    }
    get geoCoordinates() {
        return this.geometry.coordinates.map((coordinate) => GeoCoordinate.fromCoordinateArray(coordinate));
    }
    get geometry() {
        return (this.contract.geometry ?? {
            coordinates: [],
            type: 'LineString',
        });
    }
    set geometry(geometry) {
        this.contract.geometry = cloneDeep(geometry);
    }
    get isSnapped() {
        return this.properties.snapToTrail || this.properties.snapToRides;
    }
    get lengthInMeters() {
        return this.properties.lengthInMeters ?? 0;
    }
    set lengthInMeters(lengthInMeters) {
        this.properties.lengthInMeters = lengthInMeters;
    }
    get plannedRideId() {
        return this._plannedRideId;
    }
    set plannedRideId(plannedRideId) {
        this._plannedRideId = plannedRideId;
    }
    get properties() {
        if (!this.contract.properties) {
            this.contract.properties = {};
        }
        return this.contract.properties;
    }
    get segmentId() {
        return this.properties.segmentId ?? '';
    }
    get segmentType() {
        return this.properties.segmentType ?? 'OffTrail';
    }
    get waitSpinner() {
        return this.properties.waitSpinner ?? false;
    }
    set waitSpinner(val) {
        this.properties.waitSpinner = val;
    }
    static contractFromCoordinateArray(coordinateArray = [], lengthInMeters, segmentType, snapToTrail, snapToRides) {
        return {
            geometry: {
                coordinates: cloneDeep(coordinateArray),
                type: 'LineString',
            },
            properties: {
                featureType: 'RouteSegment',
                lengthInMeters: lengthInMeters || 0,
                segmentId: '',
                segmentType: segmentType || 'OffTrail',
                snapToRides,
                snapToTrail,
            },
            type: 'Feature',
        };
    }
    static fromCoordinateArray(coordinates, distance, segmentType, snapToTrail, snapToRides) {
        const plannedRideSegmentContract = PlannedRideRouteSegment.contractFromCoordinateArray(coordinates, distance, segmentType, snapToTrail, snapToRides);
        return new PlannedRideRouteSegment(plannedRideSegmentContract);
    }
    static offroadFromGeoCoordinates(startCoord, endCoord, snapToTrails = false, snapToTracks = false) {
        const plannedRideSegmentContract = PlannedRideRouteSegment.contractFromCoordinateArray([startCoord.toCoordinateArray(), endCoord.toCoordinateArray()], startCoord.distanceFrom(endCoord), 'OffTrail', snapToTrails, snapToTracks);
        return new PlannedRideRouteSegment(plannedRideSegmentContract);
    }
    toContract() {
        return cloneDeep(this.contract);
    }
    clone() {
        const plannedRideTrack = new PlannedRideRouteSegment(this.toContract());
        plannedRideTrack.plannedRideId = this.plannedRideId;
        return plannedRideTrack;
    }
    toGeoJson() {
        return {
            geometry: cloneDeep(this.geometry),
            properties: {
                featureType: 'RouteSegment',
                id: this.plannedRideId,
                segmentId: this.segmentId,
                segmentType: this.segmentType,
                waitSpinner: this.waitSpinner ? 'True' : false,
            },
            type: 'Feature',
        };
    }
}

class PlannedRideTrack {
    constructor(contract = {}) {
        this._plannedRideId = '';
        this.contract = defaultsDeep(contract, {
            properties: {
                color: 'Blue',
                featureType: 'PlannedTrack',
                routingTrailTypes: [],
                totalDurationInSeconds: 0,
                totalLengthInMeters: 0,
                vehiclePerformanceProfile: '',
            },
            type: 'Feature',
        });
        // replace existing undesignated type with designated paved
        if (this.properties.routingTrailTypes?.includes('Undesignated')) {
            this.properties.routingTrailTypes = this.properties.routingTrailTypes.filter((x) => x !== 'Undesignated');
            this.properties.routingTrailTypes.push('UndesignatedPaved');
        }
    }
    get averageElevation() {
        return this.properties.averageElevation;
    }
    get geoCoordinates() {
        return this.geometry?.coordinates.map((coordinate) => GeoCoordinate.fromCoordinateArray(coordinate)) ?? [];
    }
    get geometry() {
        return this.contract.geometry;
    }
    set geometry(geometry) {
        this.contract.geometry = cloneDeep(geometry);
    }
    get maxElevation() {
        return this.properties.maxElevation;
    }
    get minElevation() {
        return this.properties.minElevation;
    }
    get plannedRideId() {
        return this._plannedRideId;
    }
    set plannedRideId(plannedRideId) {
        this._plannedRideId = plannedRideId;
    }
    get properties() {
        if (!this.contract.properties) {
            this.contract.properties = {};
        }
        return this.contract.properties;
    }
    get rideColor() {
        return this.properties.color ?? 'Blue';
    }
    get routingTrailTypes() {
        return this.properties.routingTrailTypes;
    }
    set routingTrailTypes(val) {
        this.properties.routingTrailTypes = val;
    }
    get totalDurationInSeconds() {
        return this.properties.totalDurationInSeconds ?? 0;
    }
    set totalDurationInSeconds(duration) {
        this.properties.totalDurationInSeconds = duration;
    }
    get totalLengthInMeters() {
        return this.properties.totalLengthInMeters ?? 0;
    }
    set totalLengthInMeters(length) {
        this.properties.totalLengthInMeters = length;
    }
    get vehiclePerformanceProfile() {
        return this.properties.vehiclePerformanceProfile ?? '';
    }
    set vehiclePerformanceProfile(val) {
        this.properties.vehiclePerformanceProfile = val;
    }
    static fromCoordinateArray(coordinates, duration, length, color) {
        const plannedRideTrackContract = PlannedRideTrack.contractFromCoordinateArray(coordinates, duration, length, color);
        return new PlannedRideTrack(plannedRideTrackContract);
    }
    static contractFromCoordinateArray(coordinateArray = [], duration, length, color) {
        return {
            geometry: {
                coordinates: cloneDeep(coordinateArray),
                type: 'LineString',
            },
            properties: {
                color: color || 'Blue',
                featureType: 'PlannedTrack',
                routingTrailTypes: [],
                totalDurationInSeconds: duration || 0,
                totalLengthInMeters: length || 0,
                vehiclePerformanceProfile: '',
            },
            type: 'Feature',
        };
    }
    setColor(val) {
        this.properties.color = val;
    }
    toContract() {
        return cloneDeep(this.contract);
    }
    toGeoJson() {
        return {
            geometry: this.geometry
                ? cloneDeep(this.geometry)
                : {
                    coordinates: [],
                    type: 'LineString',
                },
            properties: {
                featureType: 'PlannedTrack',
                id: this.plannedRideId,
                totalDurationInSeconds: this.totalDurationInSeconds,
                totalLengthInMeters: this.totalLengthInMeters,
            },
            type: 'Feature',
        };
    }
    clone() {
        const plannedRideTrack = new PlannedRideTrack(this.toContract());
        plannedRideTrack.plannedRideId = this.plannedRideId;
        plannedRideTrack.totalDurationInSeconds = this.totalDurationInSeconds;
        plannedRideTrack.totalLengthInMeters = this.totalLengthInMeters;
        return plannedRideTrack;
    }
}

class PlannedRideViapoint {
    constructor(contract = {}) {
        this.name = null;
        this._plannedRideId = '';
        this.contract = contract;
    }
    get geoCoordinate() {
        return GeoCoordinate.fromCoordinateArray(this.geometry.coordinates);
    }
    get geometry() {
        if (!this.contract.geometry) {
            this.contract.geometry = {
                coordinates: [],
                type: 'Point',
            };
        }
        return cloneDeep(this.contract.geometry);
    }
    set geometry(geometry) {
        this.contract.geometry = geometry;
    }
    get id() {
        if (!this.properties.id) {
            this.properties.id = UuidService.generate();
        }
        return this.properties.id ?? '';
    }
    set id(id) {
        this.properties.id = id;
    }
    get index() {
        return this.properties.index || 0;
    }
    set index(index) {
        this.properties.index = index;
    }
    get plannedRideId() {
        return this._plannedRideId;
    }
    set plannedRideId(plannedRideId) {
        this._plannedRideId = plannedRideId;
    }
    get properties() {
        if (!this.contract.properties) {
            this.contract.properties = {};
        }
        return this.contract.properties;
    }
    static fromCoordinateArray(coordinates, properties) {
        const contract = PlannedRideViapoint.contractFromCoordinateArray(coordinates, properties);
        return new PlannedRideViapoint(contract);
    }
    static fromMapboxLngLat(lngLat, properties) {
        return PlannedRideViapoint.fromCoordinateArray([lngLat.lng, lngLat.lat], properties);
    }
    static fromLngLat(lng, lat, properties) {
        return PlannedRideViapoint.fromCoordinateArray([lng, lat], properties);
    }
    static fromGeoCoordinate(geoCoord, properties) {
        return PlannedRideViapoint.fromCoordinateArray(geoCoord.toCoordinateArray(), properties);
    }
    static fromPlannedRideWaypoint(waypoint) {
        return PlannedRideViapoint.fromCoordinateArray(waypoint.geometry.coordinates, { id: waypoint.id });
    }
    static contractFromCoordinateArray(coordinates = [], properties = {}) {
        return {
            geometry: {
                coordinates: cloneDeep(coordinates),
                type: 'Point',
            },
            properties: {
                featureType: 'Viapoint',
                id: properties.id || UuidService.generate(),
            },
            type: 'Feature',
        };
    }
    toMapboxCoordinateString() {
        const coordinates = this.geometry.coordinates;
        if (coordinates.length !== 2) {
            return '';
        }
        return coordinates.join(',');
    }
    toContract() {
        return cloneDeep(this.contract);
    }
    toGeoJson() {
        return {
            geometry: {
                coordinates: this.geometry.coordinates,
                type: 'Point',
            },
            properties: {
                featureId: this.id,
                featureType: 'Viapoint',
                id: this.plannedRideId,
                index: this.index,
            },
            type: 'Feature',
        };
    }
    clone() {
        const plannedRideViapoint = new PlannedRideViapoint(this.contract);
        plannedRideViapoint.plannedRideId = this.plannedRideId;
        return plannedRideViapoint;
    }
}

class PlannedRideWaypoint {
    static { this.coordinateArrayRegex = /^-?\d+\.\d+, ?-?\d+\.\d+$|^-?\d+,\d+, ?-?\d+,\d+$/; }
    constructor(contract = {}) {
        this._hidden = false;
        this._plannedRideId = '';
        this._stopNumber = 0;
        this._isAdhocFromSearchResult = false;
        this.contract = cloneDeep(contract);
        if (!this.properties.id) {
            this.properties.id = UuidService.generate();
        }
    }
    get description() {
        return this.properties.description || '';
    }
    set description(description) {
        this.properties.description = description;
    }
    get distance() {
        return this.properties.distance || 0;
    }
    set distance(distance) {
        this.properties.distance = distance;
    }
    get duration() {
        return this.properties.duration || 0;
    }
    set duration(duration) {
        this.properties.duration = duration;
    }
    get generatedName() {
        return this.properties.generatedName || '';
    }
    set generatedName(name) {
        this.properties.generatedName = name;
    }
    get geoCoordinate() {
        return GeoCoordinate.fromCoordinateArray(this.geometry.coordinates);
    }
    /**
     * @description Representation of the location coordinates in longitude first format.
     * Use for storing or transferring location information.
     */
    get geoJsonCoordinateString() {
        const [lng, lat] = this.geometry.coordinates;
        return `${lng}, ${lat}`;
    }
    get geometry() {
        if (!this.contract.geometry) {
            this.contract.geometry = {
                coordinates: [],
                type: 'Point',
            };
        }
        return cloneDeep(this.contract.geometry);
    }
    set geometry(geometry) {
        this.contract.geometry = geometry;
    }
    get hidden() {
        return this._hidden || false;
    }
    set hidden(hidden) {
        this._hidden = hidden;
    }
    get id() {
        return this.properties.id ?? '';
    }
    set id(id) {
        this.properties.id = id;
    }
    get index() {
        return this.properties.index || 0;
    }
    set index(index) {
        this.properties.index = index;
    }
    get isNameGenerated() {
        return this.name === this.generatedName || this.isLatLngString(this.name);
    }
    get isNameLatLngString() {
        return this.isLatLngString(this.name);
    }
    get name() {
        return this.properties.name ?? '';
    }
    set name(name) {
        this.properties.name = name;
        // if this is a generated waypoint and the user changed the name, upgrade to an adhoc waypoint
        // so that it will not get downgraded to a viapoint.
        if (this.originalType === 'generated' && !this.isNameGenerated) {
            this.originalType = 'adhocWaypoint';
        }
    }
    get originalId() {
        return this.properties.originalId || '';
    }
    set originalId(val) {
        this.properties.originalId = val;
    }
    get originalType() {
        return this.properties.originalType || {};
    }
    set originalType(originalType) {
        this.properties.originalType = originalType;
    }
    get plannedRideId() {
        return this._plannedRideId;
    }
    set plannedRideId(plannedRideId) {
        this._plannedRideId = plannedRideId;
    }
    get properties() {
        if (!this.contract.properties) {
            this.contract.properties = {};
        }
        return this.contract.properties;
    }
    get stopNumber() {
        return this._stopNumber;
    }
    set stopNumber(stopNumber) {
        this._stopNumber = stopNumber;
    }
    get isAdhocFromSearchResult() {
        return this._isAdhocFromSearchResult;
    }
    set isAdhocFromSearchResult(val) {
        this._isAdhocFromSearchResult = val;
    }
    static fromCoordinateArray(coordinates, properties, locale = defaultCulture) {
        const contract = PlannedRideWaypoint.contractFromCoordinateArray(coordinates, properties);
        const waypoint = new PlannedRideWaypoint(contract);
        if (!waypoint.properties.name) {
            const name = PlannedRideWaypoint.coordinatesToDisplayString({
                coordinates: waypoint.geometry.coordinates,
                locale,
            });
            waypoint.properties.name = name;
            waypoint.properties.generatedName = name;
        }
        return waypoint;
    }
    static fromMapboxLngLat(lngLat, properties, locale) {
        return PlannedRideWaypoint.fromCoordinateArray([lngLat.lng, lngLat.lat], properties, locale);
    }
    static fromLngLat(lng, lat, properties) {
        return PlannedRideWaypoint.fromCoordinateArray([lng, lat], properties);
    }
    static fromGeojson(geoJson) {
        return PlannedRideWaypoint.fromCoordinateArray(geoJson.geometry.coordinates, geoJson.properties);
    }
    static fromPlannedRideViapoint(viapoint, locale) {
        return PlannedRideWaypoint.fromCoordinateArray(viapoint.geometry.coordinates, { id: viapoint.id }, locale);
    }
    /** @deprecated - Use SearchService.plannedRideWaypointFromSearchResult */
    static fromSearchResult() {
        // do nothing
    }
    static contractFromCoordinateArray(coordinates = [], properties = {}) {
        return {
            geometry: {
                coordinates: cloneDeep(coordinates),
                type: 'Point',
            },
            properties: {
                description: properties.description,
                featureType: 'Waypoint',
                id: properties.id || UuidService.generate(),
                name: properties.name,
                originalId: properties.originalId,
                originalType: properties.originalType,
            },
            type: 'Feature',
        };
    }
    static coordinatesToDisplayString({ coordinates, locale = defaultCulture, }) {
        const lat = formatNumber(Number((coordinates[1] ?? 0).toFixed(6)), locale, '1.6-6');
        const lng = formatNumber(Number((coordinates[0] ?? 0).toFixed(6)), locale, '1.6-6');
        return `${lat}, ${lng}`;
    }
    /**
     * @param locale
     * @description Representation of the location coordinates in shortened, latitude first format.
     * Use for display purposes.
     */
    toShortCoordinateLocaleString(locale) {
        return PlannedRideWaypoint.coordinatesToDisplayString({ coordinates: this.geometry.coordinates, locale });
    }
    toMapboxCoordinateString() {
        const coordinates = this.geometry.coordinates;
        if (coordinates.length !== 2) {
            return '';
        }
        return `${coordinates[0]},${coordinates[1]}`;
    }
    toContract() {
        return cloneDeep(this.contract);
    }
    toGeoJson() {
        return {
            geometry: this.geometry,
            properties: {
                description: this.description,
                featureId: this.id,
                featureType: 'Waypoint',
                hidden: this.hidden,
                id: this.plannedRideId,
                index: this.index,
                name: this.name,
                originalType: this.originalType,
                stopNumber: this.stopNumber,
            },
            type: 'Feature',
        };
    }
    clone() {
        const plannedRideWaypoint = new PlannedRideWaypoint(this.contract);
        plannedRideWaypoint.distance = this.distance;
        plannedRideWaypoint.duration = this.duration;
        plannedRideWaypoint.hidden = this.hidden;
        plannedRideWaypoint.plannedRideId = this.plannedRideId;
        plannedRideWaypoint.stopNumber = this.stopNumber;
        return plannedRideWaypoint;
    }
    isLatLngString(latLngString) {
        return PlannedRideWaypoint.coordinateArrayRegex.test(latLngString);
    }
}

class PlannedRide {
    constructor(contract = {}, defaultRideType = 'ORV') {
        this.maxPoints = Infinity;
        this.type = 'PlannedRide';
        this._points = [];
        this._ride = new PlannedRideTrack();
        this._routeSegments = [];
        this._features = [];
        this.contract = cloneDeep(contract);
        this._defaultRideType = defaultRideType;
        this._defaultAppSku = defaultRideType;
        this.setFeaturesFromFeatureCollection();
        this._isInd = this.rideType === 'IND';
        this._isOrv = this.rideType === 'ORV';
        this._isSlg = this.rideType === 'SLG';
        // TODO: workaround until SNO has its own SKU
        if (this.contract.rideType) {
            this._isSno = this.rideType === 'SNO';
        }
        else {
            this._isSno = this.rideType === 'ORV' || this.rideType === 'SNO';
        }
    }
    get appSku() {
        return this.contract.appSku ?? this._defaultAppSku;
    }
    set appSku(sku) {
        this.contract.appSku = sku;
    }
    get bounds() {
        if (!this._geoBounds) {
            const coordinates = this.points.map((point) => point.geoCoordinate.toCoordinateArray());
            this._geoBounds = GeoBounds.fromCoordinateArrayPath(coordinates);
        }
        return this._geoBounds;
    }
    get color() {
        return this.ride.rideColor;
    }
    get creationDate() {
        if (!this._creationDate) {
            this._creationDate = this.contract.creationDate ? new Date(this.contract.creationDate) : new Date();
        }
        return this._creationDate;
    }
    get demoContentVersion() {
        return this.contract.demoContentVersion || '';
    }
    get description() {
        return this.contract.description || '';
    }
    set description(description) {
        this.contract.description = description;
    }
    get flyByUrl() {
        return this.contract.flyByUrl || '';
    }
    set flyByUrl(url) {
        this.contract.flyByUrl = url;
    }
    get geoCoordinate() {
        return this.isV2
            ? (this.routeSegments[0]?.geoCoordinates[0] ?? new GeoCoordinate())
            : (this.ride.geoCoordinates[0] ?? new GeoCoordinate());
    }
    get geoCoordinates() {
        return this.isV2 ? flatten(this.routeSegments.map((seg) => seg.geoCoordinates)) : this.ride.geoCoordinates;
    }
    get id() {
        return this.contract.id || '';
    }
    set id(id) {
        this.contract.id = id;
    }
    get isInd() {
        return this._isInd;
    }
    get isOrv() {
        return this._isOrv;
    }
    get isSlg() {
        return this._isSlg;
    }
    get isSno() {
        return this._isSno;
    }
    get isV2() {
        // assume v2 if there aren't the minimum number of points on the ride
        // should only be the case if editing a ride, which is only v2
        return this.hasMinimumPoints ? this.routeSegments.length > 0 : true;
    }
    get modificationDate() {
        if (!this._modificationDate) {
            this._modificationDate = this.contract.modificationDate ? new Date(this.contract.modificationDate) : new Date();
        }
        return this._modificationDate;
    }
    get name() {
        return this.contract.name || '';
    }
    set name(name) {
        this.contract.name = name;
    }
    get ownerEmail() {
        return this.contract.ownerEmail || '';
    }
    get points() {
        return this._points;
    }
    set points(points) {
        let newPoints = this.setPointPlannedRideIds(points);
        newPoints = this.setWaypointStopNumbers(newPoints);
        this._points = newPoints;
        this._geoBounds = undefined;
    }
    get pointCoordinates() {
        return this._points.map((point) => point.geometry.coordinates);
    }
    get polyLine() {
        return this.ride.geoCoordinates;
    }
    get ride() {
        return this._ride;
    }
    set ride(plannedRideTrack) {
        this._ride = this.setRideTrackPlannedRideId(plannedRideTrack);
        this._features = [this._ride, ...this._features.slice(1)];
    }
    get routeSegments() {
        return this._routeSegments;
    }
    set routeSegments(plannedRideRouteSegments) {
        this._routeSegments = this.setPlannedRideRouteSegments(plannedRideRouteSegments);
    }
    /** @deprecated - use show on map service */
    get shownOnMap() {
        return true;
    }
    get features() {
        return this._features;
    }
    set features(features) {
        this._features = features;
        this.points = features.filter((x) => x instanceof PlannedRideWaypoint || x instanceof PlannedRideViapoint);
        this._ride = features.find((x) => x instanceof PlannedRideTrack);
        this._routeSegments = features.filter((x) => x instanceof PlannedRideRouteSegment);
        this.recalculateWaypointDistances();
    }
    // when the last stop is a control point that has been temporarily
    // upgraded to a waypoint, it will be downgraded back to a control point
    // when the next point is added. In this case, the Next Waypoint number
    // is the same as the last waypoint number. When the last stop is a true
    // waypoint, the Next Waypoint number is the last waypoint number plus 1.
    // If there is only one waypoint, the next waypoint is always length + 1.
    get nextWaypoint() {
        if (this.waypoints.length === 1) {
            return this.waypoints.length + 1;
        }
        return this.waypoints.length > 0 &&
            this.waypoints[this.waypoints.length - 1].originalType === 'generated'
            ? this.waypoints.length
            : this.waypoints.length + 1;
    }
    get riderId() {
        return this.contract.riderId || '';
    }
    get rideType() {
        return this.contract.rideType ?? this._defaultRideType;
    }
    set rideType(rideType) {
        this.contract.rideType = rideType;
    }
    get snapshots() {
        return this.contract.snapshots ?? [];
    }
    get viapoints() {
        return this.points.filter((point) => point instanceof PlannedRideViapoint);
    }
    get waypoints() {
        const waypoints = this._points.filter((point) => point instanceof PlannedRideWaypoint);
        const ids = [];
        waypoints.forEach((waypoint) => {
            waypoint.hidden = includes(ids, waypoint.id);
            ids.push(waypoint.id);
        });
        return waypoints;
    }
    set waypoints(waypoints) {
        this.reOrderPoints(waypoints);
    }
    get hasMinimumPoints() {
        return this.points.length > 1;
    }
    get isWaitingOnRouting() {
        return this.routeSegments.some((x) => x.waitSpinner);
    }
    get isRouteClosedLoop() {
        if (this.waypoints.length < 2) {
            return false;
        }
        const firstCoordinates = this.waypoints[0].geoCoordinate;
        const lastCoordinates = this.waypoints[this.waypoints.length - 1].geoCoordinate;
        return (firstCoordinates.latitude === lastCoordinates.latitude && firstCoordinates.longitude === lastCoordinates.longitude);
    }
    findPointById(id) {
        return this._points.find((point) => point.id === id);
    }
    findWaypointById(id) {
        return this.waypoints.find((waypoint) => waypoint.id === id);
    }
    findViapointById(id) {
        return this.viapoints.find((viapoint) => viapoint.id === id);
    }
    addPoint(point) {
        const pointsLength = this._points.length;
        if (pointsLength >= this.maxPoints) {
            return;
        }
        point.plannedRideId = this.id;
        const points = [...this._points, point];
        this._points = this.setWaypointStopNumbers(points);
        this.features = [...this._features, point];
    }
    addPolyline(mapPolyline, startName = 'Start', endName = 'End') {
        const n = mapPolyline.polyLine.length;
        for (let i = 0; i < n; i += 1) {
            if (i === 0 || i === n - 1) {
                // Add endpoints as waypoints.
                const waypoint = PlannedRideWaypoint.fromCoordinateArray(mapPolyline.polyLine[i].toCoordinateArray(), {
                    name: i === 0 ? startName : endName,
                });
                this.addPoint(waypoint);
            }
            else {
                // Add intermediate points as viapoints.
                const viapoint = PlannedRideViapoint.fromGeoCoordinate(mapPolyline.polyLine[i]);
                this.addPoint(viapoint);
            }
        }
    }
    insertPoint(point, index) {
        if (this._points.length >= this.maxPoints) {
            return;
        }
        if (index > this._points.length - 1) {
            this.addPoint(point);
        }
        point.plannedRideId = this.id;
        const points = [...this._points.slice(0, index), point, ...this._points.slice(index)];
        this._points = this.setWaypointStopNumbers(points);
        //features should always have at least one
        this.features = [
            this.features[0],
            ...this.points,
        ];
    }
    replacePoint(oldPoint, newPoint) {
        const featureIndex = this.findFeatureIndex(oldPoint);
        if (featureIndex !== -1) {
            this.features = [...this._features.slice(0, featureIndex), newPoint, ...this._features.slice(featureIndex + 1)];
        }
        return true;
    }
    upgradeViapoint(point, permanentUpgrade = true, locale) {
        const newPoint = PlannedRideWaypoint.fromPlannedRideViapoint(point, locale);
        newPoint.originalType = permanentUpgrade ? 'adhocWaypoint' : 'generated';
        this.replacePoint(point, newPoint);
    }
    downgradePreviousEndWaypoint() {
        const previousEndpoint = this._points[this._points.length - 2];
        if (previousEndpoint &&
            previousEndpoint instanceof PlannedRideWaypoint &&
            previousEndpoint.originalType === 'generated') {
            const viapoint = PlannedRideViapoint.fromPlannedRideWaypoint(previousEndpoint);
            this.replacePoint(previousEndpoint, viapoint);
        }
    }
    clone() {
        const plannedRide = new PlannedRide(this.toContract(), this._defaultRideType);
        plannedRide.maxPoints = this.maxPoints;
        return plannedRide;
    }
    toGeoJson() {
        const points = this.points;
        const geojson = {
            features: [
                this.ride.toGeoJson(),
                ...this._routeSegments.map((segment) => segment.toGeoJson()),
                ...points.map((point) => point.toGeoJson()),
            ],
            type: 'FeatureCollection',
        };
        geojson.features.forEach((x) => {
            if (x.properties) {
                x.properties['color'] = UserContentColorHex[this.ride.rideColor];
            }
        });
        return geojson;
    }
    toContract() {
        this.setFeatureCollectionFromRideAndPoints();
        return cloneDeep(this.contract);
    }
    isFinalWaypoint(waypoint) {
        if (this._points.length >= this.maxPoints) {
            return true;
        }
        return this.findPointIndex(waypoint) === this._points.length - 1;
    }
    getNextStopNumber(point) {
        const index = this._points.indexOf(point);
        let stopNumber = 0;
        for (let i = index; i < this._points.length; i += 1) {
            if (this._points[i] instanceof PlannedRideWaypoint) {
                stopNumber = this._points[i].stopNumber;
                break;
            }
        }
        return stopNumber;
    }
    // discard ride.geometry,
    // create route segments as off trail straight lines between points
    getV2Version() {
        const newPlannedRide = new PlannedRide(this.contract, this._defaultRideType);
        const newFeatures = [];
        for (let i = 0; i < this.points.length; i += 1) {
            newFeatures.push(this.points[i]);
            const nextPoint = this.points[i + 1];
            if (nextPoint) {
                const currentCoord = this.points[i].geoCoordinate;
                const nextCoord = nextPoint.geoCoordinate;
                newFeatures.push(PlannedRideRouteSegment.offroadFromGeoCoordinates(currentCoord, nextCoord));
            }
        }
        const newRide = this.ride.clone();
        newPlannedRide.features = [newRide, ...newFeatures];
        return newPlannedRide;
    }
    getNextWaypointIndex(segmentId) {
        const segmentIndex = this.features.findIndex((x) => x instanceof PlannedRideRouteSegment && x.segmentId === segmentId);
        for (let i = segmentIndex; i < this.features.length; i += 1) {
            if (this.features[i] instanceof PlannedRideViapoint || this.features[i] instanceof PlannedRideWaypoint) {
                return this.points.indexOf(this.features[i]);
            }
        }
        return -1;
    }
    isNextPointViapoint(point) {
        const index = this.findPointIndex(point);
        return this.points[index + 1] instanceof PlannedRideViapoint;
    }
    hasSurroundingViapoint(viapoint) {
        const i = this.findPointIndex(viapoint);
        return ((i !== 0 && this.points[i - 1] instanceof PlannedRideViapoint) ||
            this.points[i + 1] instanceof PlannedRideViapoint);
    }
    reOrderPoints(newWaypoints) {
        const oldPoints = this.points;
        const oldWaypoints = oldPoints.filter((point) => point instanceof PlannedRideWaypoint);
        const firstWaypointIdx = this.findIndexOfFirstChangedWaypoint(oldWaypoints, newWaypoints);
        const lastWaypointIdx = this.findIndexOfLastChangedWaypoint(oldWaypoints, newWaypoints);
        const changedPoints = newWaypoints.slice(Math.max(firstWaypointIdx - 1, 0), Math.min(lastWaypointIdx + 2, newWaypoints.length));
        const firstPointIdx = oldPoints.indexOf(oldWaypoints[Math.max(firstWaypointIdx - 1, 0)]);
        const lastPointIdx = oldPoints.indexOf(oldWaypoints[Math.min(lastWaypointIdx + 1, oldWaypoints.length - 1)]);
        const unchangedPointsStart = oldPoints.slice(0, firstPointIdx);
        const unchangedPointsEnd = oldPoints.slice(lastPointIdx + 1);
        this.points = [...unchangedPointsStart, ...changedPoints, ...unchangedPointsEnd];
        this.features = [
            this.features[0],
            ...this.points,
        ];
    }
    findIndexOfFirstChangedWaypoint(oldWaypoints, newWaypoints) {
        let i = 0;
        for (const len = oldWaypoints.length; i < len; i += 1) {
            if (!isEqual(oldWaypoints[i]?.geometry.coordinates, newWaypoints[i]?.geometry.coordinates)) {
                break;
            }
        }
        if (i === oldWaypoints.length) {
            return oldWaypoints.length - 1;
        }
        return i;
    }
    findFeatureIndex(point) {
        let foundIndex = this._features.indexOf(point);
        if (foundIndex === -1 && point.id) {
            foundIndex = findIndex(this._features, { id: point.id });
        }
        return foundIndex;
    }
    findIndexOfLastChangedWaypoint(oldWaypoints, newWaypoints) {
        let i = oldWaypoints.length - 1;
        for (; i >= 0; i -= 1) {
            if (!isEqual(oldWaypoints[i]?.geometry.coordinates, newWaypoints[i]?.geometry.coordinates)) {
                break;
            }
        }
        if (i === -1) {
            return oldWaypoints.length - 1;
        }
        return i;
    }
    findPointIndex(point) {
        let foundIndex = this._points.indexOf(point);
        if (foundIndex === -1 && point.id) {
            foundIndex = findIndex(this._points, { id: point.id });
        }
        return foundIndex;
    }
    recalculateWaypointDistances() {
        // if v1 - use distances from saved distance for v1
        // if the feature data is missing (community items), use the saved distance
        if (!this.isV2 || !this._features.some((feat) => feat instanceof PlannedRideWaypoint)) {
            return;
        }
        let distanceBetweenWaypoints = 0;
        const newRide = this.ride.clone();
        let previousWaypoint;
        let sumOfAllDistanceBetweenWaypoints = 0;
        for (const feature of this._features) {
            if (feature instanceof PlannedRideWaypoint) {
                if (previousWaypoint) {
                    previousWaypoint.distance = distanceBetweenWaypoints;
                    sumOfAllDistanceBetweenWaypoints += distanceBetweenWaypoints;
                    distanceBetweenWaypoints = 0;
                    previousWaypoint = feature;
                }
                else {
                    previousWaypoint = feature;
                }
            }
            else if (feature instanceof PlannedRideRouteSegment) {
                distanceBetweenWaypoints += feature.lengthInMeters;
            }
        }
        newRide.totalLengthInMeters = sumOfAllDistanceBetweenWaypoints;
        this.ride = newRide;
    }
    setFeaturesFromFeatureCollection() {
        const segments = [];
        let track;
        let points = [];
        const features = [];
        const feats = get(this.contract.featureCollection, 'features', []);
        if (feats.length === 0) {
            track = new PlannedRideTrack();
            features.push(track);
        }
        for (const feature of feats) {
            if (feature.properties?.['featureType'] === 'PlannedTrack' && !track) {
                track = new PlannedRideTrack(feature);
                track.plannedRideId = this.id;
                // put track upfront of feature array
                features.unshift(track);
            }
            else if (feature.properties?.['featureType'] === 'RouteSegment') {
                const segment = new PlannedRideRouteSegment(feature);
                segment.plannedRideId = this.id;
                segments.push(segment);
                features.push(segment);
            }
            else if (feature.geometry?.type === 'Point') {
                points.push(feature.properties?.['featureType'] === 'Waypoint'
                    ? new PlannedRideWaypoint(feature)
                    : new PlannedRideViapoint(feature));
                features.push(points[points.length - 1]);
            }
        }
        // assign a new index if there are more than 1 points and every points is default
        if (points.length > 1 && points.every((x) => x.index === 0)) {
            points.forEach((point, index) => (point.index = index));
        }
        else {
            points = sortBy(points, 'index');
        }
        this._ride = track ?? new PlannedRideTrack();
        this._routeSegments = segments;
        this.points = points;
        this._features = features;
        this.recalculateWaypointDistances();
    }
    setFeatureCollectionFromRideAndPoints() {
        if (!this.contract.featureCollection) {
            this.contract.featureCollection = { features: [], type: 'FeatureCollection' };
            this.contract.type = this.type;
        }
        this.contract.featureCollection.features = this.features.map((feature) => {
            return feature.toContract();
        });
    }
    setPointPlannedRideIds(points) {
        return points.map((point) => {
            point.plannedRideId = this.id;
            return point;
        });
    }
    setRideTrackPlannedRideId(rideTrack) {
        const ride = rideTrack.clone();
        ride.plannedRideId = this.id;
        return ride;
    }
    setPlannedRideRouteSegments(segments) {
        const segs = segments.map((x) => x.clone());
        segs.forEach((x) => (x.plannedRideId = this.id));
        return segs;
    }
    setWaypointStopNumbers(points) {
        let index = 0;
        let stopNumber = 1;
        return points.map((point) => {
            if (point instanceof PlannedRideWaypoint) {
                point.stopNumber = stopNumber;
                stopNumber += 1;
            }
            point.index = index;
            index += 1;
            return point;
        });
    }
}

class ShowOnMapItem {
    constructor(json = {}) {
        this.id = '';
        this.showOnMap = false;
        Object.assign(this, json);
    }
    clone() {
        return new ShowOnMapItem(this);
    }
}

class ShouldShowRegistry {
    constructor(items) {
        this.items = items.map((item) => new ShowOnMapItem(item));
    }
    getShouldShow(id) {
        const item = this.getItem(id);
        return item.showOnMap;
    }
    setShouldShow(id, shouldShow) {
        const item = this.getItem(id);
        item.showOnMap = shouldShow;
        return new ShouldShowRegistry([...this.items.filter((i) => i.id !== id), item]);
    }
    toJson() {
        return JSON.stringify(this.items);
    }
    getItem(id) {
        return this.items.find((item) => item.id === id) ?? new ShowOnMapItem({ id, showOnMap: true });
    }
}

class ShowOnMapService {
    constructor(userStoreService) {
        this.userStoreService = userStoreService;
        this.showOnMapKey = 'showOnMap';
    }
    getShouldShow(id) {
        const registry = this.getRegistry();
        return registry.getShouldShow(id);
    }
    setShouldShow(id, shouldShow) {
        const registry = this.getRegistry();
        this.shouldShowRegistry = registry.setShouldShow(id, shouldShow);
        this.saveRegistry(this.shouldShowRegistry);
    }
    setHidden(id) {
        this.setShouldShow(id, false);
    }
    setVisible(id) {
        this.setShouldShow(id, true);
    }
    toggleVisibility(id) {
        this.setShouldShow(id, !this.getShouldShow(id));
    }
    getRegistry() {
        if (!this.shouldShowRegistry) {
            const json = this.userStoreService.get(this.showOnMapKey);
            let parsed;
            try {
                if (typeof json === 'string') {
                    parsed = JSON.parse(json);
                }
                else {
                    parsed = [];
                }
            }
            catch {
                parsed = [];
            }
            this.shouldShowRegistry = new ShouldShowRegistry(parsed);
        }
        return this.shouldShowRegistry;
    }
    saveRegistry(registry) {
        this.userStoreService.set(this.showOnMapKey, registry.toJson());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ShowOnMapService, deps: [{ token: i3.UserStoreService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ShowOnMapService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ShowOnMapService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i3.UserStoreService }] });

class PlannedRideResourceService {
    constructor(config, routingHelpersService, http) {
        this.http = http;
        this.plannedRideUrl = routingHelpersService.combineUrl(config.apiUrl.rideCommand, 'v2/planned-rides');
    }
    getPlannedRides() {
        const url = `${this.plannedRideUrl}?includeDeleted=false`;
        return this.http.get(url);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: PlannedRideResourceService, deps: [{ token: i1.ConfigService }, { token: i2.RoutingHelpersService }, { token: i3.WebComponentHttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: PlannedRideResourceService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: PlannedRideResourceService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1.ConfigService }, { type: i2.RoutingHelpersService }, { type: i3.WebComponentHttpClient }] });

class PlannedRideService {
    constructor(authService, config, plannedRideResourceService, showOnMapService) {
        this.authService = authService;
        this.config = config;
        this.plannedRideResourceService = plannedRideResourceService;
        this.showOnMapService = showOnMapService;
        this.plannedRidesCache = new CacheCollectionObservable();
    }
    getPlannedRides$(shouldForce = false) {
        const cached = this.plannedRidesCache.getItem();
        return this.authService.isAuthenticated$().pipe(switchMap((isAuthenticated) => {
            if (!isAuthenticated) {
                return of([]);
            }
            if (shouldForce || !cached) {
                return this.plannedRidesCache.setItem(this.plannedRideResourceService.getPlannedRides().pipe(switchMap((contracts) => {
                    const plannedRides = contracts.map((contract) => new PlannedRide(contract, this.config.appSku));
                    return of(plannedRides);
                })));
            }
            return cached;
        }));
    }
    getShowOnMapStatus(id) {
        return this.showOnMapService.getShouldShow(id);
    }
    setShowOnMapStatus(id, shouldShow) {
        this.showOnMapService.setShouldShow(id, shouldShow);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: PlannedRideService, deps: [{ token: i1$1.AuthService }, { token: i1.ConfigService }, { token: PlannedRideResourceService }, { token: ShowOnMapService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: PlannedRideService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: PlannedRideService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1$1.AuthService }, { type: i1.ConfigService }, { type: PlannedRideResourceService }, { type: ShowOnMapService }] });

class TrackedRide {
    constructor(contract = {}, defaultRideType = 'ORV') {
        this.type = 'TrackedRide';
        this._images = [];
        this._polyLine = [];
        this.contract = cloneDeep(contract);
        this._defaultRideType = defaultRideType;
        this._isInd = this.rideType === 'IND';
        this._isOrv = this.rideType === 'ORV';
        this._isSlg = this.rideType === 'SLG';
        // TODO: workaround until SNO has its own SKU
        if (this.properties.rideType) {
            this._isSno = this.rideType === 'SNO';
        }
        else {
            this._isSno = this.rideType === 'ORV';
        }
    }
    get appSku() {
        return this.properties.appSku ?? 'ORV';
    }
    get averageElevation() {
        return this.properties.averageElevation;
    }
    get averageSpeed() {
        return this.properties.averageSpeed;
    }
    get bounds() {
        if (!this._bounds) {
            const coordinates = this.coordinates;
            this._bounds = GeoBounds.fromCoordinateArrayPath(coordinates);
        }
        return this._bounds;
    }
    get color() {
        return this.properties.color;
    }
    get coordinates() {
        if (this.geometry.coordinates.length <= 0) {
            this.geometry.coordinates = [];
        }
        return this.geometry.coordinates;
    }
    get creationDate() {
        if (!this._creationDate) {
            this._creationDate = this.properties.creationDate ? new Date(this.properties.creationDate) : new Date();
        }
        return this._creationDate;
    }
    get description() {
        return this.properties.description || '';
    }
    set description(value) {
        this.properties.description = value;
    }
    get elevationChange() {
        if (typeof this.maxElevation === 'undefined' || typeof this.minElevation === 'undefined') {
            return undefined;
        }
        return this.maxElevation - this.minElevation;
    }
    get flyByUrl() {
        return this.properties.flyByUrl || '';
    }
    set flyByUrl(url) {
        this.properties.flyByUrl = url;
    }
    get geoCoordinate() {
        const coords = this.coordinates[0];
        return GeoCoordinate.fromCoordinateArray(coords);
    }
    get geometry() {
        if (!this.contract.geometry) {
            this.contract.geometry = {
                coordinates: [],
                type: 'LineString',
            };
        }
        return this.contract.geometry;
    }
    get id() {
        return this.properties.id || '';
    }
    set id(id) {
        this.properties.id = id;
    }
    get images() {
        if (this._images.length === 0 && (this.properties.images?.length || 0) > 0) {
            this._images = (this.properties.images || []).map((imageContract) => new Image(imageContract));
        }
        return this._images;
    }
    get isInd() {
        return this._isInd;
    }
    get isOrv() {
        return this._isOrv;
    }
    get isSlg() {
        return this._isSlg;
    }
    get isSno() {
        return this._isSno;
    }
    get maxElevation() {
        return this.properties.maxElevation;
    }
    get maxSpeed() {
        return this.properties.maxSpeed;
    }
    get minElevation() {
        return this.properties.minElevation;
    }
    get modificationDate() {
        if (!this._modificationDate) {
            this._modificationDate = this.properties.modificationDate
                ? new Date(this.properties.modificationDate)
                : new Date();
        }
        return this._modificationDate;
    }
    get movingTimeInSeconds() {
        if (typeof this.totalDurationSeconds === 'undefined' || typeof this.stoppedTimeInSeconds === 'undefined') {
            return undefined;
        }
        return this.totalDurationSeconds - this.stoppedTimeInSeconds;
    }
    get name() {
        return this.properties.name || '';
    }
    set name(value) {
        this.properties.name = value;
    }
    get polyLine() {
        if (this._polyLine.length === 0 && this.coordinates.length > 0) {
            this._polyLine = this.coordinates.map((coord) => GeoCoordinate.fromCoordinateArray(coord));
        }
        return this._polyLine;
    }
    get properties() {
        if (!this.contract.properties) {
            this.contract.properties = {};
        }
        return this.contract.properties;
    }
    get riderId() {
        return this.properties.riderId || '';
    }
    get rideType() {
        return this.properties.rideType || this._defaultRideType;
    }
    set rideType(rideType) {
        this.properties.rideType = rideType;
    }
    /* @deprecated - this property is only available from a share, which can wrap a tracked ride. */
    get sharedBy() {
        throw new Error('not implemented');
    }
    /** @deprecated - use show on map service */
    get shownOnMap() {
        return true;
    }
    get snapshots() {
        return this.properties.snapshots ?? [];
    }
    get stoppedTimeInSeconds() {
        return this.properties.stoppedTimeInSeconds;
    }
    /**
     * @description Duration of tracked ride in seconds. Null/undefined values are significant, do not default to zero.
     */
    get totalDurationSeconds() {
        return this.properties.totalDurationSeconds ?? 0;
    }
    /**
     * @description Length of tracked ride in meters. Null/undefined values are significant, do not default to zero.
     */
    get totalLengthMeters() {
        return this.properties.totalLengthMeters ?? 0;
    }
    static fromCoordinateArray(coordinates, defaultRideType) {
        return new TrackedRide({ geometry: { coordinates, type: 'LineString' } }, defaultRideType);
    }
    clone() {
        return new TrackedRide(this.contract, this._defaultRideType);
    }
    setColor(color) {
        this.properties.color = color;
    }
    toGeoJson() {
        return {
            geometry: this.contract.geometry,
            properties: { ...this.properties },
            type: this.contract.type,
        };
    }
    toGeoJsonPoint() {
        const properties = { id: this.id };
        if (this.color) {
            const color = UserContentColorHex[this.color];
            if (color) {
                properties.color = color;
            }
        }
        return {
            geometry: {
                coordinates: this.geoCoordinate.toCoordinateArray(),
                type: 'Point',
            },
            properties,
            type: 'Feature',
        };
    }
    toContract() {
        return cloneDeep(this.contract);
    }
    toJson() {
        return this.toContract();
    }
    updateModifiedDate() {
        const currentDate = new Date();
        if (currentDate.getTime() > new Date(this.properties.modificationDate ?? 0).getTime()) {
            this.properties.modificationDate = currentDate.toISOString();
        }
    }
    toRoutingRequestUserTrackContract() {
        return {
            geometry: this.contract.geometry,
            properties: {
                id: this.properties.id,
                trackType: 'Unknown',
            },
            type: this.geometry.type,
        };
    }
}

class TrackedRideResourceService {
    constructor(config, routingHelpersService, http) {
        this.http = http;
        this.baseUrl = routingHelpersService.combineUrl(config.apiUrl.rideCommand, 'v2/rides');
    }
    getTrackedRidesForUser() {
        return this.http.get(`${this.baseUrl}?includeDeleted=false`);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TrackedRideResourceService, deps: [{ token: i1.ConfigService }, { token: i2.RoutingHelpersService }, { token: i3.WebComponentHttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TrackedRideResourceService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TrackedRideResourceService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1.ConfigService }, { type: i2.RoutingHelpersService }, { type: i3.WebComponentHttpClient }] });

class TrackedRideService {
    constructor(authService, config, logService, showOnMapService, trackedRideResource) {
        this.authService = authService;
        this.config = config;
        this.logService = logService;
        this.showOnMapService = showOnMapService;
        this.trackedRideResource = trackedRideResource;
        this.className = 'TrackedRideService';
        this.trackedRidesCache = new CacheCollectionObservable();
    }
    getTrackedRides$(shouldForce = false) {
        const cached = this.trackedRidesCache.getItem();
        return this.authService.isAuthenticated$().pipe(switchMap((isAuthenticated) => {
            if (!isAuthenticated) {
                return of([]);
            }
            if (shouldForce || !cached) {
                return this.trackedRidesCache.setItem(this.trackedRideResource.getTrackedRidesForUser().pipe(switchMap((contracts) => {
                    const rideCollection = new GeoJsonFeatureCollection(contracts);
                    const rideContracts = rideCollection.features;
                    const trackedRides = rideContracts.map((contract) => {
                        return new TrackedRide(contract, this.config.appSku);
                    });
                    return of(trackedRides);
                }), catchError((error) => {
                    this.logService.error(this.className, error);
                    return of([]);
                })));
            }
            return cached;
        }));
    }
    getShowOnMapStatus(id) {
        return this.showOnMapService.getShouldShow(id);
    }
    setShowOnMapStatus(id, shouldShow) {
        this.showOnMapService.setShouldShow(id, shouldShow);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TrackedRideService, deps: [{ token: i1$1.AuthService }, { token: i1.ConfigService }, { token: i2.LogService }, { token: ShowOnMapService }, { token: TrackedRideResourceService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TrackedRideService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TrackedRideService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1$1.AuthService }, { type: i1.ConfigService }, { type: i2.LogService }, { type: ShowOnMapService }, { type: TrackedRideResourceService }] });

// import { Injectable } from '@angular/core';
// import { AuthService } from '@polaris-customerweb/sharedcomponents.auth';
// import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
// import { EntityType, RiderColor } from '@polaris-customerweb/sharedcomponents.core';
// import {
//   PlannedRideService,
//   TrackedRideService,
// } from '@polaris-customerweb/sharedcomponents.rides';
// import { UserContentItem } from '@polaris-customerweb/sharedcomponents.shared';
// import { UserProfileService } from '@polaris-customerweb/sharedcomponents.user-profile';
// import { InProgressRideService } from '@polaris-customerweb/sharedcomponents.vehicle-health';
// export class GetUserContentResult {
//   userContentItem: UserContentItem | undefined;
//   communityItem?: CommunityItem;
//   share?: Share;
//   rideEvent?: RideEvent;
//   collection?: Collection;
//   isCommunityItem = false;
//   isCommunityItemChild = false;
//   isShare = false;
//   isShareChild = false;
//   constructor(userContentItem?: UserContentItem) {
//     this.userContentItem = userContentItem;
//   }
// }
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
class UserContentService {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: UserContentService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: UserContentService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: UserContentService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class Waypoint {
    static { this.publishedState = {
        /* eslint-disable sort-keys */
        public: 0,
        private: 1,
        pending: 3,
        /* eslint-enable sort-keys */
    }; }
    static { this.iconToFontSymbolDictionary = {
        /* eslint-disable sort-keys */
        1: '\ue900',
        2: '\ue901',
        3: '\ue902',
        4: '\ue903',
        5: '\ue904',
        6: '\ue905',
        7: '\ue906',
        8: '\ue907',
        9: '\ue908',
        10: '\ue909',
        11: '\ue90a',
        12: '\ue90b',
        13: '\ue90c',
        14: '\ue90d',
        15: '\ue90e',
        16: '\ue90f',
        17: '\ue910',
        18: '\ue911',
        19: '\ue912',
        20: '\ue913',
        21: '\ue914',
        22: '\ue915',
        23: '\ue916',
        24: '\ue917',
        /* eslint-enable sort-keys */
        a: '\ue918',
        airport: '\ue919',
        antelope: '\ue91a',
        attraction: '\ue91b',
        automotive: '\ue91c',
        b: '\ue91d',
        bank: '\ue91e',
        bar: '\ue91f',
        beach: '\ue920',
        bear: '\ue921',
        bedding: '\ue922',
        blind: '\ue923',
        blood_trail: '\ue924',
        boar: '\ue925',
        bridge: '\ue926',
        business: '\ue927',
        c: '\ue928',
        camping: '\ue929',
        community: '\ue92a',
        d: '\ue92b',
        dealership: '\ue92c',
        deer: '\ue92d',
        drinking_water: '\ue92e',
        e: '\ue92f',
        elk: '\ue930',
        entertainment: '\ue931',
        evac: '\ue97d',
        f: '\ue932',
        feeder: '\ue933',
        fishing: '\ue934',
        food_source: '\ue935',
        forest: '\ue936',
        fowl: '\ue937',
        g: '\ue938',
        game_trail: '\ue939',
        gas: '\ue93a',
        gate: '\ue93b',
        h: '\ue93c',
        hazzard: '\ue93d',
        historic_site: '\ue93e',
        i: '\ue93f',
        ice_fishing: '\ue940',
        j: '\ue941',
        junction: '\ue942',
        k: '\ue943',
        l: '\ue944',
        left_turn: '\ue945',
        lodging: '\ue946',
        m: '\ue947',
        marine: '\ue948',
        mark_outline: '\ue949',
        mark_solid: '\ue94a',
        meadow: '\ue94b',
        medical: '\ue94c',
        mud: '\ue94d',
        n: '\ue94e',
        o: '\ue94f',
        p: '\ue950',
        parking: '\ue951',
        picnic_area: '\ue952',
        q: '\ue953',
        r: '\ue954',
        recreation: '\ue955',
        restaurant: '\ue956',
        restrooms: '\ue957',
        right_turn: '\ue958',
        rub: '\ue959',
        rv_parking: '\ue95a',
        s: '\ue95b',
        scat: '\ue95c',
        scenic: '\ue95d',
        shed_antler: '\ue95e',
        shopping: '\ue95f',
        shot: '\ue960',
        ski: '\ue961',
        small_game: '\ue962',
        stand: '\ue963',
        straight: '\ue964',
        stream_crossing: '\ue965',
        structure: '\ue966',
        summit: '\ue967',
        swimming: '\ue968',
        t: '\ue969',
        tracks: '\ue96a',
        trail_camera: '\ue96b',
        trailhead: '\ue96c',
        transportation: '\ue96d',
        trap: '\ue96e',
        turkey: '\ue96f',
        u: '\ue971',
        u_turn: '\ue970',
        v: '\ue972',
        vehicle: '\ue973',
        viewpoint: '\ue974',
        w: '\ue975',
        wash: '\ue976',
        water: '\ue978',
        water_source: '\ue977',
        x: '\ue979',
        y: '\ue97a',
        z: '\ue97b',
    }; }
    constructor(contract = {}, defaultRideType = 'ORV') {
        this.type = 'Waypoint';
        this._color = 'Blue';
        this._defaultRideType = 'ORV';
        this._geoCoordinate = new GeoCoordinate();
        this.rawWaypoint = { ...contract };
        this._defaultRideType = defaultRideType;
        this._creationDate = this.rawWaypoint.updatedate ? new Date(this.rawWaypoint.updatedate) : new Date();
        this._geoCoordinate = GeoCoordinate.fromLongitudeAndLatitude(this.rawWaypoint.loc?.lon || 0, this.rawWaypoint.loc?.lat || 0);
        // put in all the lazy loaded properties in -- remove get()
        if (this.rideTypes.length >= 0) {
            this._isInd = this._defaultRideType === 'IND';
            this._isOrv = this._defaultRideType === 'ORV';
            this._isSlg = this._defaultRideType === 'SLG';
            // TODO: switch to SNO when SNO has its own SKU
            this._isSno = this._defaultRideType === 'ORV';
        }
        else {
            this._isInd = intersection(this.rideTypes, ['IND']).length > 0;
            this._isOrv = intersection(this.rideTypes, ['ORV', 'ATV', 'RGR', 'RZR', 'GEN']).length > 0;
            this._isSlg = intersection(this.rideTypes, ['SLG']).length > 0;
            this._isSno = intersection(this.rideTypes, ['SNO']).length > 0;
        }
    }
    get bounds() {
        return GeoBounds.fromGeoCoordinates([this.geoCoordinate]);
    }
    get color() {
        this._color = this.isPublic
            ? (this.rawWaypoint.color || '#7F4F04')
            : this.rawWaypoint.tintForTagIcon || 'Blue';
        return this._color;
    }
    set color(color) {
        this._color = color;
        // this.rawWaypoint.color = color;
    }
    get description() {
        return this.rawWaypoint.description || '';
    }
    set description(description) {
        this.rawWaypoint.description = description;
    }
    get creationDate() {
        return this._creationDate;
    }
    get geoCoordinate() {
        return this._geoCoordinate;
    }
    set geoCoordinate(geoCoordinate) {
        this._geoCoordinate = geoCoordinate;
        this.rawWaypoint.loc = {
            lat: geoCoordinate.lat,
            lon: geoCoordinate.lng,
        };
    }
    get icon() {
        return get(Waypoint.iconToFontSymbolDictionary, first(this.tags) || '', get(Waypoint.iconToFontSymbolDictionary, 'mark_solid'));
    }
    get id() {
        return this.rawWaypoint.id || '';
    }
    set id(id) {
        this.rawWaypoint.id = id;
    }
    get imageurl() {
        return this.rawWaypoint.imageurl || '';
    }
    set imageurl(imageurl) {
        this.rawWaypoint.imageurl = imageurl;
    }
    get isInd() {
        return this._isInd;
    }
    get isOrv() {
        return this._isOrv;
    }
    get isPublic() {
        return this.rawWaypoint.publicstatus === Waypoint.publishedState.public;
    }
    get isSlg() {
        return this._isSlg;
    }
    get isSno() {
        return this._isSno;
    }
    get modificationDate() {
        if (!this._modificationDate && this.rawWaypoint.modificationDate) {
            this._modificationDate = new Date(this.rawWaypoint.modificationDate);
        }
        return this._modificationDate;
    }
    get name() {
        return this.rawWaypoint.name || '';
    }
    set name(name) {
        this.rawWaypoint.name = name;
    }
    get publicstatus() {
        return this.rawWaypoint.publicstatus || 0;
    }
    set publicstatus(publicStatus) {
        this.rawWaypoint.publicstatus = publicStatus;
    }
    get riderId() {
        return this.rawWaypoint.userid || '';
    }
    get rideTypes() {
        return this.rawWaypoint.rideTypes ? [...this.rawWaypoint.rideTypes] : [];
    }
    set rideTypes(rideTypes) {
        this.rawWaypoint.rideTypes = [...rideTypes];
    }
    /** @deprecated - use shown on map service */
    get shownOnMap() {
        return true;
    }
    get snapshots() {
        return this.rawWaypoint.snapshots ? [...this.rawWaypoint.snapshots] : [];
    }
    get tags() {
        return this.rawWaypoint.tags ? [...this.rawWaypoint.tags] : [];
    }
    set tags(tags) {
        this.rawWaypoint.tags = [...tags];
    }
    get tintForTagIcon() {
        return this.rawWaypoint.tintForTagIcon || 'Blue';
    }
    set tintForTagIcon(color) {
        this.rawWaypoint.tintForTagIcon = color;
    }
    static createFromLngLat(lng, lat, defaultRideType) {
        return new Waypoint({
            loc: {
                lat,
                lon: lng,
            },
        }, defaultRideType);
    }
    clone() {
        return new Waypoint(this.toContract(), this._defaultRideType);
    }
    toGeoJson() {
        return {
            geometry: this.geoCoordinate.toGeoJsonGeometryPoint(),
            properties: {
                color: this.isPublic ? this.color : UserContentColorHex[this.color],
                description: this.description,
                id: this.id,
                name: this.name,
                type: this.icon,
            },
            type: 'Feature',
        };
    }
    toContract() {
        return this.rawWaypoint;
    }
    updateModifiedDate() {
        this.rawWaypoint.modificationDate = new Date().toISOString();
    }
}

// import { forkJoin, Observable, of } from 'rxjs';
// import {
//     catchError,
//     map,
//     switchMap,
//     tap,
//     withLatestFrom,
// } from 'rxjs/operators';
// import { WaypointResourceService } from './waypoint-resource.service';
// import { GeoBounds, GeoJsonFeatureCollection } from '../geo';
// import { MapEventPoint, VehicleType } from '@polaris-customerweb/sharedcomponents.core';
// import { Waypoint } from './waypoint';
// import { isUndefined } from 'lodash-es';
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
class WaypointService {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: WaypointService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: WaypointService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: WaypointService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { Geofence, MapDetailBottomButtonBarComponent, PlanRideButtonComponent, PlannedRide, PlannedRideRouteSegment, PlannedRideService, PlannedRideTrack, PlannedRideViapoint, PlannedRideWaypoint, SaveShareButtonComponent, Share, ShareItem, ShareItemCopyResponse, ShareResourceService, ShareService, ShouldShowRegistry, ShowOnMapItem, ShowOnMapService, TrackedRide, TrackedRideService, UserContentService, Waypoint, WaypointService };
